'use strict';

import React, {
    Component
} from 'react';
import {
    bindActionCreators,
    Dispatch
} from 'redux';
import {connect} from 'react-redux';
import memoize from 'memoize-one';
import {v4 as uuidV4} from 'uuid';
import _ from 'lodash';
import {keys} from 'ts-transformer-keys';


import {
    TreeTableExpandedKeysType
} from 'primereact/treetable';
import {
    IconUtils
} from 'primereact/utils';
import {
    ConfirmPopup,
    confirmPopup
} from 'primereact/confirmpopup';
import {ContextMenu} from 'primereact/contextmenu';
import {
    MenuItem,
    MenuItemCommandEvent
} from 'primereact/menuitem';
import {Button} from 'primereact/button';
import TreeNode from 'primereact/treenode';
import {ChevronDownIcon} from 'primereact/icons/chevrondown';
import {ChevronRightIcon} from 'primereact/icons/chevronright';
import {Tooltip} from 'primereact/tooltip';
import {
    Tree,
    TreeMultipleSelectionKeys,
    TreeNodeTemplateOptions,
    TreeTogglerTemplateOptions
} from 'primereact/tree';
import {Panel} from 'primereact/panel';
import {
    Checkbox,
    CheckboxChangeEvent
} from 'primereact/checkbox';
import {Toolbar} from 'primereact/toolbar';


import {__} from '~/shared/src/utilities/messageCatalog';
import metaModel from '~/shared/src/utilities/metaModel';
import Item, {
    ItemAttributesForCreation,
    ItemCollection
} from '~/shared/src/ci/Item';
import Reference, {
    ReferenceAttributesForCreation,
    ReferenceCollection
} from '~/shared/src/ci/Reference';
import ReferenceSet from '~/shared/src/mm/ReferenceSet';
import User from '~/shared/src/model/User';
import {ExtendTreeNode} from '~/shared/src/utilities/modules/tree';
import objectUtils from '~/shared/src/utilities/objectUtils';
import dataExtraction from '~/shared/src/utilities/dataExtraction';
import {fieldTypeToItemTypeMapping} from '~/shared/src/utilities/modules/form';
import {
    aliasSeparator,
    buildDataDescriptor,
    DataDescriptor,
    DataObjectProperties,
    ReferenceConfiguration,
    StaticDataDescriptor
} from '~/shared/src/utilities/dataStructure';
import {LoadColumnDataDescriptorsParameters} from '~/shared/src/utilities/modules/overview';
import {
    BaseRequestParameters,
    defaultBaseRequestParameters
} from '~/shared/src/utilities/modules/common';
import {Query} from '~/shared/src/utilities/query';

import {MergeMode} from '~/frontend/src/utilities/dataUtils';
import TreeUtils, {
    NodeMap,
    NodePermissions
} from '~/frontend/src/utilities/TreeUtils';
import keyboardUtils from '~/frontend/src/utilities/keyboardUtils';
import {getPanelProps} from '~/frontend/src/components/base/page/PageTabPanel/utilities';
import ActionMixin, {
    ActionMixinInterface
} from '~/frontend/src/utilities/ActionMixin';
import {
    getIconSpan,
    getItemKeyDisplayName,
    getItemNameDisplayName,
    resolveLigatureIcon
} from '~/frontend/src/utilities/renderHelpers';
import getAddItemDialog, {
    ComponentProps as AddTreeNodeDialogProps
} from '~/frontend/src/components/base/shared/tree/ItemTree/AddTreeNodePanel';
import {
    ReferenceType as OverviewDesignerReferenceType,
    DragOptions
} from '~/frontend/src/components/base/OverviewDesigner';
import {overviewUtilities} from '~/frontend/src/components/base/OverviewDesigner/utilities';
import ContentButton, {ReferenceType as ContentButtonType} from '~/frontend/src/components/base/shared/ContentButton';
import {
    getToolbarComponents,
    ToolbarComponents,
    ToolbarLayoutComponents
} from '~/frontend/src/utilities/toolbars';

import {
    StoreState,
    Thunk
} from '~/frontend/src/store';
import * as ElusiveDataActions from '~/frontend/src/actions/elusiveData';
import {
    ElusiveDataState,
    PanelElusiveDataState
} from '~/frontend/src/reducers/elusiveData';
import openPanel, {OpenPanel} from '~/frontend/src/actions/panelManager/openPanel';
import setPanelProps from '~/frontend/src/actions/panelManager/setPanelProps';
import openPanelForItemType, {OpenPanelForItemType} from '~/frontend/src/actions/panelManager/openPanelForItemType';
import * as PanelActions from '~/frontend/src/actions/panel/common';
import {SetParamAction} from '~/frontend/src/actions/panel/common/setParam';
import {UnsetParamAction} from '~/frontend/src/actions/panel/common/unsetParam';
import {SetParamsAction} from '~/frontend/src/actions/panel/common/setParams';
import * as TreeActions from '~/frontend/src/actions/panel/treeView';
import loadData, {LoadData} from '~/frontend/src/actions/data/loadData';
import addItem, {AddItem} from '~/frontend/src/actions/data/addItem';
import deleteItem, {DeleteItem} from '~/frontend/src/actions/data/deleteItem';
import updateItem, {UpdateItem} from '~/frontend/src/actions/data/updateItem';
import addReference, {AddReference} from '~/frontend/src/actions/data/addReference';
import deleteReference, {DeleteReference} from '~/frontend/src/actions/data/deleteReference';
import updateReference, {UpdateReference} from '~/frontend/src/actions/data/updateReference';
import * as IntercomActions from '~/frontend/src/actions/intercom';
import {
    ElusiveData,
    ClearElusiveData,
    SetElusiveData
} from '~/frontend/src/actions/elusiveData';
import {PanelIntercomState} from '~/frontend/src/reducers/intercom';
import {
    SetMessage,
    unsetMessage,
    UnsetMessage
} from '~/frontend/src/actions/intercom';
import {XhrComponentState} from '~/frontend/src/reducers/xhr';


type DataConfig = {
    itemTypeName : string;
    key : string;
    name : string;
    dataObjectId? : number;
    properties? : {
        [ propertyTypeName : string ] : AttributeValue;
    };
    enumProperties? : {
        [ propertyTypeName : string ] : {
            key? : string;
            value? : AttributeValue;
        }[]
    };
    additionalData? : ( DataConfig & {
        referenceTypeName : string;
        referenceDirection : 'src' | 'dest';
    } )[];
};

type PayloadConfig = {
    itemId : number;
    itemTypeName : string;
    itemTypeLabel : string;
    schema : Schema;
    referenceTypeName? : string;
    referenceDirection? : 'SrcToDest' | 'DestToSrc';
    // true if payload reference from child to parent treennode is reverse to the referenceset direction
    reverseDirection? : boolean;
    cardinality? : string;
    referenceLabel : string;
    referenceSet? : ReferenceSet;
};

type ColumnDefinition = {
    type : 'item' | 'property' | 'image' | 'meta' | 'reference' | 'referenceProperty' | 'referenceImage' | 'referenceMeta' | 'query';
    key : string;
    label : string;
    icon : string;
    dataDescriptorAlias? : string[];
    data : DataConfig;
};

type ExtendedTreeNodeData = Omit<TreeNode.TreeNode, 'data'> & Omit<TreeNodeData, 'icon'> & {
    icon? : TreeNode.TreeNode['icon'];
    data : TreeNodeData[ 'data' ] & {
        columnDefinition : ColumnDefinition[];

        isDeleted? : boolean;
        buttons? : React.ReactNode[];
        dataDescriptorAlias? : string[];
        payloadConfig? : PayloadConfig;
    };
};

type Variables = {
    [ variable : string ] : any;
};

type PanelElusiveData = PanelElusiveDataState & PanelErrorData & PanelDirtyData & {
    variables? : Variables;
    scrollPositions? : {
        [ treeNodeDataKey : string ] : number;
    },
    query? : Query;
    columnDataDescriptors? : StaticDataDescriptor[];
};

export type DefaultProps = BaseRequestParameters & {

    readOnly? : boolean;

    apiCommand? : string;

    apiParams? : {
        [ key : string ] : string | number | boolean
    };

    // props to be passed to ItemTree
    expandedKeys? : TreeTableExpandedKeysType;

    disableLoader? : boolean;

    useElusiveDataFromDataContext? : boolean;

    /**
     * Additional context to be updated (elusive data)
     */
    additionalElusiveDataContexts? : string[];

    scrollTop? : number;
    scrollLeft? : number;

    /**
     * set to true if existing data should be kept on reload.
     * Needed if multiple trees are used in a single context, e.g. ViewDesigner
     */
    keepExistingOnReload? : boolean;

    showTypeLabels? : boolean;
    showReferenceFields? : boolean;
    showMetaFields? : boolean;
};
export const defaultProps : Required<DefaultProps> = {
    ...defaultBaseRequestParameters,

    readOnly                     : false,
    apiCommand                   : 'tree',
    apiParams                    : {},
    expandedKeys                 : {},
    disableLoader                : false,
    useElusiveDataFromDataContext: true,
    additionalElusiveDataContexts: [],
    scrollTop                    : 0,
    scrollLeft                   : 0,
    keepExistingOnReload         : false,
    showTypeLabels               : false,
    showReferenceFields          : false,
    showMetaFields               : false
};

export type Props = {
    uid : string;
    parentUid : string;

    dataContext : string;

    className : string;

    style : CssStyle;

    /**
     * The title to display the panel with. If not set the title will be computed.
     */
    title? : string;

    /**
     * The title to display the panel with. If not set the title will be computed.
     */
    tabTitle? : string;

    /**
     * The title to display the panel with. If not set the title will be computed.
     */
    tooltipTitle? : string;

    /**
     * The icon to display the panel with. If not set the title will be computed.
     */
    iconCls? : string;

    onLoad? : () => void;

    onGetTreeUtils? : ( uid : string, treeUtils : TreeUtils ) => void;

    onNewLayoutElementDragStart? : OverviewDesignerReferenceType['startDrag'];

    onMarkLayoutElementsForAlias? : ( alias : string | string[] | undefined ) => void;

    selectionKeys? : string[];

    selectedLayoutElementId? : number;

    /**
     * Callback if a layout element is selected.
     *
     * @param id
     */
    onSelectLayoutElement? : ( id? : number ) => void;

    // changing treeUtils stuff for performance boost on switching tabs.
    treeData? : ExtendTreeNode[];
    flatNodeMap? : NodeMap;
    flatDataObjectNodeMap? : NodeMap;
    payloadIdToDynamicNodeIdMap? : {
        [ p : number ] : string[]
    };

    subPanelErrors? : {
        [ subPanelKey : string ] : ErrorData
    };

    nodeIdToPanelKeyMap? : {
        [ nodeId : string ] : string
    };

    resumedNodes? : {
        [ nodeId : string ] : boolean
    };

    filter? : boolean,

    filterValue? : string

    /**
     * Function invoked if form should render a new item (rootItemID < 0) and no global.data therefor existing.
     */
    onInvalidData? : () => void;

    /**
     * The transformer for the module (component) state.
     * NOTE: Have to be set (in opposition to be a default) to be included in the state anyway!
     */
    moduleTransformer? : string;
};
export type ComponentProps = DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

type StateProps = {

    /**
     * The full elusiveData
     */
    allElusiveData? : ElusiveDataState;

    /**
     * The elusive data (not persisted by redux store) for this component.
     *
     * Will be mapped from global state.
     */
    elusiveData? : PanelElusiveData;

    /**
     * The intercom messages for this component.
     *
     * Will be mapped from global state.
     */
    intercomMessages? : PanelIntercomState;

    messageCatalogTimestamp? : number;

    metaModelTimestamp? : number;

    /**
     * The current user.
     *
     * Will be mapped from global state.
     */
    user? : User;

    /**
     * The running requests belonging to this component (uid).
     *
     * Will be mapped from global state.
     */
    xhrRequestsForComponent? : XhrComponentState;
};

/**
 * Function props mapped by mapDispatchToProps
 */
type ActionPropsRaw = {

    // panel manager actions
    // DO NOT REMOVE - not necessarily used in this file BUT needed by ActionMixin (ActionMixinInterface)
    openPanel : OpenPanel;
    openPanelForItemType : OpenPanelForItemType;

    addItem : AddItem;
    deleteItem : DeleteItem;
    updateItem : UpdateItem;
    addReference : AddReference;
    deleteReference : DeleteReference;
    updateReference : UpdateReference;

    // intercom actions
    setMessage : SetMessage;
    unsetMessage : UnsetMessage;

    setElusiveData : SetElusiveData;
    clearElusiveData : ClearElusiveData;
    loadData : LoadData;
    unsetParam : UnsetParam;
    setParams : SetParams;
};
type ReplaceReturnType<T extends ( ...a : any ) => any, TNewReturn> = ( ...a : Parameters<T> ) => TNewReturn;
type ActionProps = {
    [K in keyof ActionPropsRaw] : ReplaceReturnType<ActionPropsRaw[K], ReturnType<ActionPropsRaw[K]> extends Thunk<any> ? ReturnType<ReturnType<ActionPropsRaw[K]>> : void>;
} & {
    setParam : SetParam;
};

const Actions = Object.assign( { loadData, unsetMessage, openPanel, openPanelForItemType }, PanelActions, TreeActions, IntercomActions, ElusiveDataActions, { addItem, deleteItem, updateItem, addReference, deleteReference, updateReference } );
type SetParam = <K extends keyof AllProps>( panelKey : string, paramKey : K, paramValue : AllProps[K] ) => SetParamAction;
type UnsetParam = ( panelKey : string, paramKey : keyof AllProps ) => UnsetParamAction;
type SetParams = ( panelKey : string, panelProps : Partial<AllProps & any> ) => SetParamsAction;

export type AllProps = Required<DefaultProps> & Props & StateProps & ActionProps;

type ContextMenuConfig = Partial<AddTreeNodeDialogProps> & NodePermissions;

const initialState = {
    // the ciId of the currently selected tree node
    ciId                       : false as boolean | number,
    clearData                  : false as boolean,
    contextMenuNode            : '' as string,
    lastContextMenuNode        : '' as string,
    nodeToCopy                 : '' as string,
    nodeToMove                 : '' as string,
    addMode                    : 'new' as ( 'new' | 'existing' | 'clone' ),
    nodeIdToErrorDataMap       : {} as {
        [ nodeId : string ] : ErrorData | undefined
    },
    metaDataContext            : '' as string,
    addItemDialogConfig        : {} as ContextMenuConfig,
    deferRender                : false as boolean,
    runsInDesignerContext      : false as boolean,
    lastSelectedLayoutElementId: undefined as number | undefined,
    aliasToMark                : undefined as string | string[] | undefined,
    selectedDataDescriptorAlias: undefined as string | undefined
};

type State = typeof initialState;

// define interface for TreeTable component to apply methods from mixin
export interface TreeTable extends Component<AllProps, State>, ActionMixinInterface {
}

/**
 * Tree component that renders a tree of Items
 */
export class TreeTable extends Component<AllProps, State> {

    static defaultProps = defaultProps;

    state = Object.assign( {}, initialState );

    variables? : Variables;

    doSetScrollPosition : boolean = false;
    debouncedGetScrollPosition : () => void;

    debug : boolean = false;
    renderStartTime : number = 0;

    nodeButtons : { [ buttonId : string ] : { justInteracted : boolean, content? : ContentButtonType } } = {};
    nodeButtonPanels : { [ panelId : string ] : Panel } = {};
    ignoreSelectionChange : boolean = false;

    cm? : ContextMenu | null;

    treeUtils? : TreeUtils;

    treeElement? : HTMLDivElement;

    lastRenderedLanguage? : string;

    nodeKeyToDataDescriptorAliasesRepresenting : {
        [ nodeKey : string ] : string[]
    } = {};

    getTreeUtils = memoize(
        function( uid : string, dataContext : string, elusiveData? : ElusiveData, user? : User ) {
            // TODO: Frage: Ist es gut in einem unauffälligen getter ein LoadData zu triggern?
            const treeUtils = new TreeUtils( uid, dataContext, elusiveData, this.props.flatNodeMap, this.props.flatDataObjectNodeMap, user, this.debug );
            // fire onLoad event if new data has been loaded
            // use timeout to decouple onLoad error to avoid potential state changes in render cycle
            setTimeout( () => {
                this.props.onGetTreeUtils?.( this.props.uid, treeUtils );
                if ( elusiveData ) {
                    this.props.onLoad?.();
                }
            }, 10 );
            return treeUtils;
        }.bind( this ), ( newArgs, lastArgs ) => {
            let retVal = newArgs[ 0 ] === lastArgs[ 0 ];
            retVal = retVal && _.isEqual( newArgs[ 2 ]?.metaData?.cypherConfig ?? {}, lastArgs[ 2 ]?.metaData?.cypherConfig ?? {} );
            return retVal;
        }
    );

    /**
     * React Component constructor
     * @param {Object} props
     */
    constructor( props : AllProps ) {
        super( props );

        this.addNode = this.addNode.bind( this );
        this.deleteOrUndeleteNode = this.deleteOrUndeleteNode.bind( this );
        this.getTreeContextMenu = this.getTreeContextMenu.bind( this );
        this.markFieldsForAlias = this.markFieldsForAlias.bind( this );
        this.onLoad = this.onLoad.bind( this );
        this.onShowMetaFieldsCheckboxChange = this.onShowMetaFieldsCheckboxChange.bind( this );
        this.onShowReferenceFieldsCheckboxChange = this.onShowReferenceFieldsCheckboxChange.bind( this );
        this.onShowTypeLabelsCheckboxChange = this.onShowTypeLabelsCheckboxChange.bind( this );
        this.openNewPanel = this.openNewPanel.bind( this );
        this.treeNodeTemplate = this.treeNodeTemplate.bind( this );
        this.treeTogglerTemplate = this.treeTogglerTemplate.bind( this );

        this.debouncedGetScrollPosition = _.debounce( this.getScrollPosition.bind( this ), 250 );

        this.getFooter = memoize( this.getFooter );

        if ( this.props.treeData && global.data[ this.props.dataContext ]?.items ) {
            this.treeUtils = this.getTreeUtils( this.props.uid, this.props.dataContext, this.props.elusiveData, this.props.user );
            this.treeUtils.setData( this.props.apiParams.rootItemId as number, this.props.treeData as TreeNodeData[], this.props.payloadIdToDynamicNodeIdMap );
        }

        // TODO: decide when to autoload data on component initialization => didMount
        if ( !this.props.disableLoader && ( _.isEmpty( this.props.elusiveData?.data ) || !global.data[ this.props.dataContext ] ) ) {
            const { apiParams, dataContext } = this.props,
                  rootItemId                 = apiParams.rootItemId as number ?? 0;

            if ( rootItemId <= 0 && !apiParams.rootItemKey && !global.data[ dataContext ]?.items[ rootItemId ] ) {
                if ( this.props.onInvalidData ) {
                    this.props.onInvalidData();
                }
            }
            else {
                void this.loadData();
            }
        }

        if ( this.debug ) {
            this.renderStartTime = performance.now();
        }
    }

    /**
     * REACT lifecycle method
     */
    static getDerivedStateFromProps( props : AllProps, state : State ) {
        const retVal : Partial<State> = {},
              {
                  subPanelErrors,
                  nodeIdToPanelKeyMap,
                  dataContext,
                  uid,
                  selectedLayoutElementId
              }                       = props;

        retVal.nodeIdToErrorDataMap = nodeIdToPanelKeyMap ? _.mapValues( nodeIdToPanelKeyMap, panelKey => subPanelErrors?.[ panelKey ] ) : {};
        retVal.runsInDesignerContext = uid?.startsWith( 'ViewDesigner' );
        retVal.metaDataContext = dataContext + '-meta';

        if ( selectedLayoutElementId !== state.lastSelectedLayoutElementId || !state.selectedDataDescriptorAlias ) {
            if ( selectedLayoutElementId ) {
                const selectedLayoutElementItem = global.data[ dataContext ]?.items[ selectedLayoutElementId ];

                if ( selectedLayoutElementItem ) {
                    const selectedLayoutElementItemAttributes = dataExtraction.extractPropertiesOfItem( selectedLayoutElementItem );

                    let dataObjectFound = false;

                    for ( const itemReferenceId of selectedLayoutElementItem.references ) {
                        const reference                                    = global.data[ dataContext ]?.references?.[ itemReferenceId ],
                              dataObjectProvidesDataForItemReferenceTypeId = metaModel[ selectedLayoutElementItem.schema ].getReferenceSetsForItemTypesAndReferenceType( 'DataObject', selectedLayoutElementItem.itemTypeId, 'providesDataFor', 'src' )?.[ 0 ]?.id;

                        if (
                            reference
                            && reference.destItemId === selectedLayoutElementItem.id
                            && reference.referenceSetId === dataObjectProvidesDataForItemReferenceTypeId
                            && reference.srcItem
                        ) {
                            const dataObjectAttributes = dataExtraction.extractPropertiesOfItem( reference.srcItem ),
                                  attributePath        = selectedLayoutElementItemAttributes.attributePath as string | undefined,
                                  dataDescriptor       = attributePath && dataObjectAttributes ? buildDataDescriptor( { attributePath }, { item: dataObjectAttributes } as unknown as DataObjectProperties ) : undefined;

                            if ( dataDescriptor?.alias ) {
                                dataObjectFound = true;
                                retVal.selectedDataDescriptorAlias = dataDescriptor.alias;
                            }

                            break;
                        }
                    }

                    if ( !dataObjectFound && selectedLayoutElementItemAttributes.queryFieldName ) {
                        retVal.selectedDataDescriptorAlias = selectedLayoutElementItemAttributes.queryFieldName;
                    }
                }
            }
            else {
                retVal.selectedDataDescriptorAlias = undefined;
            }
        }

        retVal.lastSelectedLayoutElementId = props.selectedLayoutElementId;

        return retVal;
    }

    /**
     * REACT lifecycle method
     */
    componentDidMount() {
        this.props.setParam( this.props.uid, 'moduleTransformer', 'tree' );

        if ( this.props.useElusiveDataFromDataContext ) {
            this.doSetScrollPosition = true;
            document.addEventListener( 'scroll', this.debouncedGetScrollPosition, true );
            this.setScrollPosition();
        }

        if ( this.treeElement ) {
            this.treeElement.onkeydown = this.onKeyDown.bind( this );
        }

        this.checkForIntercomMessages();

        if ( !this.props.treeData?.length || !global.data[ this.props.dataContext ]?.items ) {
            void this.buildTree();
        }

        this.checkQuery();
    }

    /**
     * REACT lifecycle method
     */
    componentWillUnmount() {
        if ( this.props.useElusiveDataFromDataContext ) {
            document.removeEventListener( 'scroll', this.debouncedGetScrollPosition, true );
            this.getScrollPosition();
        }
    }

    /**
     * REACT lifecycle method
     */
    componentDidUpdate() {
        this.checkForIntercomMessages();

        this.checkQuery();

        // scrolling
        if ( this.props.useElusiveDataFromDataContext ) {
            this.setScrollPosition();
        }

        for ( const id of Object.keys( this.props.elusiveData?.scrollPositions ?? {} ) ) {
            const element = document.getElementById( `${id}_scrollContainer` );
            if ( element ) {
                element.scrollTop = this.props.elusiveData!.scrollPositions![ id ];
            }
        }

        // mark/unmark aliasToMark
        // remove current
        let elements : HTMLCollectionOf<Element> | NodeListOf<Element> | null | undefined = document.querySelectorAll( `.yn-menu-tree#${this.props.uid}_tree .yn-designertree-field.yn-designertree-field-using-alias` ),
            index                                                                         = 0;

        if ( elements?.length ) {
            while ( elements[ index ] ) {
                elements[ index++ ].classList.remove( 'yn-designertree-field-using-alias' );
            }
        }

        elements = document.querySelectorAll( `.yn-menu-tree#${this.props.uid}_tree .yn-designertree-header-using-alias` );
        index = 0;

        if ( elements?.length ) {
            while ( elements[ index ] ) {
                elements[ index++ ].classList.remove( 'yn-designertree-header-using-alias' );
            }
        }

        // mark
        if ( this.state.aliasToMark ) {
            const aliasesToMark = Array.isArray( this.state.aliasToMark ) ? this.state.aliasToMark : [ this.state.aliasToMark ];

            for ( const aliasToMark of aliasesToMark ) {
                const selector : string[] = [
                    `.yn-menu-tree#${this.props.uid}_tree .yn-designertree-field[data-datadescriptoralias~="${aliasToMark}"]`,
                    `.yn-menu-tree#${this.props.uid}_tree .yn-designertree-field[data-datadescriptoralias^="${aliasToMark}"]`,
                    `.yn-menu-tree#${this.props.uid}_tree .yn-designertree-field[data-datadescriptoralias$="${aliasToMark}"]`
                ];

                elements = document.querySelectorAll( selector.join( ', ' ) );
                index = 0;

                if ( elements?.length ) {
                    while ( elements[ index ] ) {
                        //elements[ index ].scrollIntoView( { behavior: 'smooth', block: 'nearest' } );
                        elements[ index++ ].classList.add( 'yn-designertree-field-using-alias' );
                    }
                }
                else {
                    for ( const nodeKey of Object.keys( this.nodeKeyToDataDescriptorAliasesRepresenting ) as string[] ) {
                        if ( this.nodeKeyToDataDescriptorAliasesRepresenting[ nodeKey ].includes( aliasToMark ) ) {
                            const element = document.getElementById( nodeKey );

                            //element?.scrollIntoView( { behavior: 'smooth', block: 'nearest' } );
                            element?.classList.add( 'yn-designertree-header-using-alias' );
                        }
                    }
                }
            }
        }
    }

    /**
     * REACT lifecycle method
     */
    shouldComponentUpdate( nextProps : Readonly<AllProps>, nextState : Readonly<State>, nextContext : any ) : boolean {
        const startTime         = this.debug ? performance.now() : 0,
              changedProps      = objectUtils.getDiffKeys(
                  this.props,
                  nextProps,
                  [
                      'treeData',
                      'flatNodeMap',
                      'flatDataObjectNodeMap',
                      'elusiveData',
                      'elusiveData.data'
                  ],
                  [
                      'elusiveData'
                  ],
                  [
                      'onNewLayoutElementDragStart',
                      'onMarkLayoutElementsForAlias',
                      'onSelectLayoutElement'
                  ]
              ),
              changedState      = objectUtils.getDiffKeys(
                  this.state,
                  nextState
              ),
              allowRebuild      = !nextProps.treeData || changedProps.includes( 'elusiveData.data' ) || changedProps.includes( 'messageCatalogTimestamp' ) || changedProps.includes( 'metaModelTimestamp' ) || nextProps.user?.language !== this.lastRenderedLanguage || ( !this.props.elusiveData?.data && nextProps.elusiveData?.data ),
              rootItemIdChanged = changedProps.includes( 'apiParams' ) && nextProps.apiParams.rootItemId !== this.props.apiParams.rootItemId;

        let deferRender            = nextState.deferRender,
            updateSelectionKeyPath = false;

        if ( this.debug && this.renderStartTime === 0 ) {
            this.renderStartTime = startTime;
        }

        if ( rootItemIdChanged ) {
            deferRender = true;

            // wrap function execution in a deferRender block
            const asyncLoad = async () => {
                await new Promise( resolve => {
                    this.setState( { deferRender: true }, () => resolve( true ) );
                } );

                await this.loadData();

                await new Promise( resolve => {
                    this.setState( { deferRender: false }, () => resolve( true ) );
                } );
            };

            void asyncLoad();
        }

        // run tree functions if something relevant has been changed
        if (
            allowRebuild
            && !deferRender
            && nextProps.elusiveData?.query?.type !== 'SQL'
            && nextProps.elusiveData?.query?.type !== 'Cypher'
        ) {
            deferRender = true;

            // wrap function execution in a deferRender block
            const asyncBuild = async () => {
                await new Promise( resolve => {
                    this.setState( { deferRender: true }, () => resolve( true ) );
                } );

                await this.buildTree( nextProps, nextState );

                await new Promise( resolve => {
                    this.setState( { deferRender: false }, () => resolve( true ) );
                } );
            };

            if ( this.debug ) {
                console.log( '    asyncBuild' );
            }

            void asyncBuild();

            this.lastRenderedLanguage = user?.language;
        }

        if ( changedProps.includes( 'selectionKeys' ) && this.treeUtils ) {
            const selectionKeysToPath : { [ key : string ] : string[] } = {};

            if ( nextProps.selectionKeys?.length ) {
                for ( const key of nextProps.selectionKeys ) {
                    if ( nextProps.elusiveData?.selectionKeysToPath?.[ key ] ) {
                        selectionKeysToPath[ key ] = nextProps.elusiveData.selectionKeysToPath[ key ];
                    }
                    else {
                        const node = this.treeUtils.getNodeById( key );

                        if ( node?.data.payloadIdPath ) {
                            selectionKeysToPath[ key ] = ( node.data.payloadIdPath as string ).split( /-(-?\d+)/ );
                        }
                    }
                }
            }

            if ( JSON.stringify( nextProps.elusiveData?.selectionKeysToPath ?? {} ) !== JSON.stringify( selectionKeysToPath ) ) {
                if ( this.props.useElusiveDataFromDataContext ) {
                    this.props.setElusiveData( nextProps.dataContext, { selectionKeysToPath } );
                }
                else {
                    this.props.setElusiveData( nextProps.uid, { selectionKeysToPath } );
                }
                updateSelectionKeyPath = true;
            }
        }

        const retVal = !updateSelectionKeyPath && ( !deferRender || changedState.includes( 'addItemDialogConfig' ) || changedState.includes( 'addMode' ) ) && ( !!changedState.length || !!changedProps.length );

        if ( this.debug ) {
            console.log( '    changedProps', changedProps );
            console.log( '    changedState', changedState );
            console.log( '    retVal', retVal );
            console.log( `ItemTree - shouldComponentUpdate - ${Math.round( 1000 * ( performance.now() - startTime ) ) / 1000} ms`.asLogLine( 100 ) );
        }

        return retVal;
    }

    getPayloadTreeData( treeUtils : TreeUtils, payloadRootItemId : number, elusiveData? : ElusiveData, newPayloadReferenceId? : number, user? : User ) : ExtendTreeNode[] {
        const {
                  dataContext,
                  expandedKeys,
                  readOnly
              }                               = this.props,
              newExpandedKeys                 = { ...expandedKeys ?? {} },
              dynamicTreeNodes                = treeUtils.applyData( payloadRootItemId, newExpandedKeys, newPayloadReferenceId ),
              dynamicElements                 = dynamicTreeNodes[ 0 ]?.children?.[ 0 ]?.children?.[ 0 ] as ExtendedTreeNodeData,
              processDynamicElementsRecursive = ( nodeData : ExtendedTreeNodeData ) => {
                  if ( nodeData.key && nodeData.data?.payloadId ) {
                      const payloadItem = global.data[ dataContext ]?.items[ nodeData.data.payloadId ];

                      if ( payloadItem?.itemType.name === 'DataObject' ) {
                          const dataObjectAttributes   = dataExtraction.extractPropertiesOfItem( payloadItem ),
                                payloadConfig          = this.getPayloadConfig( nodeData as ExtendTreeNode ),
                                currentItemTypeName    = payloadConfig?.itemTypeName,
                                currentItemId          = payloadConfig?.itemId ?? 0,
                                items                  = global.data[ dataContext ]?.items,
                                currentItem            = items?.[ currentItemId ],
                                rootItemId             = elusiveData?.data?.rootItemId,
                                payloadReferenceId     = nodeData.data?.payloadReferenceId,
                                payloadReference       = payloadReferenceId ? global.data[ dataContext ]?.references[ payloadReferenceId ] : undefined,
                                schema                 = ( payloadConfig?.schema ?? dataObjectAttributes.objectSchema ?? 'data' ).toUpperCase() as Schema,
                                allowedReferenceSetIds = currentItemTypeName ? this.getAllowedReferenceSetsForItemType( currentItemId ) : {},
                                nodeIsDeleted          = ( payloadReference ?? currentItem )?.isDeleted(),
                                addItemConfig          = this.getAddItemDialogConfig( currentItemTypeName, allowedReferenceSetIds, nodeData as ExtendTreeNode, schema ),
                                allowAdd               = !addItemConfig.disableAddExistingItem || !addItemConfig.disableAddNewItem,
                                allowDelete            = !!payloadConfig?.referenceTypeName && ( !addItemConfig.disableDeleteReference || !addItemConfig.disableDeleteItem || currentItemId < 0 );

                          let attributePath : string,
                              dataDescriptor : DataDescriptor | undefined,
                              dataDescriptorAliases : string[] | undefined;

                          if ( ( payloadConfig?.itemTypeLabel === 'Message' || payloadConfig?.itemTypeLabel === 'Image' ) && payloadConfig?.referenceTypeName ) {
                              nodeData.label = `${_.upperFirst( dataExtraction.extractIsForReferenceTypeName( payloadConfig?.referenceTypeName ) )} (${payloadConfig.itemTypeLabel})`;
                          }
                          else if ( payloadConfig?.itemTypeLabel ) {
                              nodeData.label = payloadConfig.itemTypeLabel;
                          }

                          if ( nodeIsDeleted ) {
                              nodeData.selectable = false;
                          }
                          nodeData.className = 'yn-dataobject-node';
                          nodeData.icon = resolveLigatureIcon( `yn-tree-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( dataObjectAttributes.objectType ?? '' )?.icon}` );
                          nodeData.data.isDeleted = nodeIsDeleted;
                          nodeData.data.buttons = [];
                          nodeData.data.dataDescriptorAlias = [];
                          nodeData.data.columnDefinition = [];
                          nodeData.data.payloadConfig = payloadConfig;

                          // dataDescriptorAlias
                          attributePath = 'item.id';
                          dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectAttributes } as unknown as DataObjectProperties );
                          if ( dataDescriptor?.alias ) {
                              nodeData.data.dataDescriptorAlias.push( dataDescriptor.alias.substring( 0, dataDescriptor.alias.indexOf( aliasSeparator ) ) );
                          }
                          attributePath = 'reference.id';
                          dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectAttributes } as unknown as DataObjectProperties );
                          if ( dataDescriptor?.alias ) {
                              nodeData.data.dataDescriptorAlias.push( dataDescriptor.alias.substring( 0, dataDescriptor.alias.indexOf( aliasSeparator ) ) );
                          }

                          // add button
                          if ( !readOnly && allowAdd && !nodeIsDeleted ) {
                              nodeData.data.buttons.push( this.getAddNodeButton( nodeData.id, currentItemId ) );
                          }
                          // delete/restore button
                          if ( !readOnly && allowDelete && currentItemId !== rootItemId ) {
                              nodeData.data.buttons.push( this.getDeleteNodeButton( nodeData.id ) );
                          }

                          this.nodeKeyToDataDescriptorAliasesRepresenting[ nodeData.key ] = [];

                          const propertySets = metaModel[ schema ].getPropertySetsForItemType( dataObjectAttributes.objectType );

                          propertySets.sort(
                              ( pset1, pset2 ) => pset1.label.toLowerCase() > pset2.label.toLowerCase() ? 1 : ( pset1.label.toLowerCase() < pset2.label.toLowerCase() ? -1 : 0 )
                          );


                          // reference data
                          if ( payloadConfig?.referenceSet ) {

                              // reference attributes
                              attributePath = 'reference.id';
                              dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectAttributes } as unknown as DataObjectProperties );
                              dataDescriptorAliases = [];

                              if ( dataDescriptor?.alias ) {
                                  dataDescriptorAliases.push( dataDescriptor.alias );
                                  this.nodeKeyToDataDescriptorAliasesRepresenting[ nodeData.key ].push( dataDescriptor.alias );
                              }
                              if ( dataDescriptor?.fullAlias ) {
                                  dataDescriptorAliases = dataDescriptorAliases.concat( dataDescriptor?.fullAlias.split( ',' ) );
                              }

                              nodeData.data.columnDefinition.push( {
                                  type               : 'reference',
                                  key                : 'id',
                                  label              : __( 'YN.base.common.attribute.id' ),
                                  icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'IntegerField' )?.icon}`,
                                  dataDescriptorAlias: dataDescriptorAliases,
                                  data               : {
                                      itemTypeName: 'Column',
                                      key         : `${payloadConfig.referenceSet.shortName}:id`,
                                      name        : `${payloadConfig.referenceSet.shortName} id`,
                                      dataObjectId: dataObjectAttributes.id,
                                      properties  : {
                                          AttributePath: attributePath
                                      }
                                  }
                              } );


                              // reference meta
                              attributePath = 'reference.referenceTypeName';
                              dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectAttributes } as unknown as DataObjectProperties );
                              dataDescriptorAliases = [];

                              if ( dataDescriptor?.alias ) {
                                  dataDescriptorAliases.push( dataDescriptor.alias );
                                  this.nodeKeyToDataDescriptorAliasesRepresenting[ nodeData.key ].push( dataDescriptor.alias );
                              }
                              if ( dataDescriptor?.fullAlias ) {
                                  dataDescriptorAliases = dataDescriptorAliases.concat( dataDescriptor?.fullAlias.split( ',' ) );
                              }

                              nodeData.data.columnDefinition.push( {
                                  type               : 'referenceMeta',
                                  key                : 'referenceTypeName',
                                  label              : __( 'YN.base.common.attribute.referenceTypeName' ),
                                  icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'StringField' )?.icon}`,
                                  dataDescriptorAlias: dataDescriptorAliases,
                                  data               : {
                                      itemTypeName: 'Column',
                                      key         : `${payloadConfig.referenceSet.shortName}:referenceTypeName`,
                                      name        : `${payloadConfig.referenceSet.shortName} referenceTypeName`,
                                      dataObjectId: dataObjectAttributes.id,
                                      properties  : {
                                          AttributePath: attributePath
                                      }
                                  }
                              } );

                              attributePath = 'reference.referenceSetId';
                              dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectAttributes } as unknown as DataObjectProperties );
                              dataDescriptorAliases = [];

                              if ( dataDescriptor?.alias ) {
                                  dataDescriptorAliases.push( dataDescriptor.alias );
                                  this.nodeKeyToDataDescriptorAliasesRepresenting[ nodeData.key ].push( dataDescriptor.alias );
                              }
                              if ( dataDescriptor?.fullAlias ) {
                                  dataDescriptorAliases = dataDescriptorAliases.concat( dataDescriptor?.fullAlias.split( ',' ) );
                              }

                              nodeData.data.columnDefinition.push( {
                                  type               : 'referenceMeta',
                                  key                : 'referenceSetId',
                                  label              : __( 'YN.base.common.attribute.referenceSetId' ),
                                  icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'IntegerField' )?.icon}`,
                                  dataDescriptorAlias: dataDescriptorAliases,
                                  data               : {
                                      itemTypeName: 'Column',
                                      key         : `${payloadConfig.referenceSet.shortName}:referenceSetId`,
                                      name        : `${payloadConfig.referenceSet.shortName} referenceSetId`,
                                      dataObjectId: dataObjectAttributes.id,
                                      properties  : {
                                          AttributePath: attributePath
                                      }
                                  }
                              } );

                              attributePath = 'reference.statusId';
                              dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectAttributes } as unknown as DataObjectProperties );
                              dataDescriptorAliases = [];

                              if ( dataDescriptor?.alias ) {
                                  dataDescriptorAliases.push( dataDescriptor.alias );
                                  this.nodeKeyToDataDescriptorAliasesRepresenting[ nodeData.key ].push( dataDescriptor.alias );
                              }
                              if ( dataDescriptor?.fullAlias ) {
                                  dataDescriptorAliases = dataDescriptorAliases.concat( dataDescriptor?.fullAlias.split( ',' ) );
                              }

                              nodeData.data.columnDefinition.push( {
                                  type               : 'referenceMeta',
                                  key                : 'statusId',
                                  label              : __( 'YN.base.common.attribute.statusId' ),
                                  icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'IntegerField' )?.icon}`,
                                  dataDescriptorAlias: dataDescriptorAliases,
                                  data               : {
                                      itemTypeName: 'Column',
                                      key         : `${payloadConfig.referenceSet.shortName}:statusId`,
                                      name        : `${payloadConfig.referenceSet.shortName} statusId`,
                                      dataObjectId: dataObjectAttributes.id,
                                      properties  : {
                                          AttributePath: attributePath
                                      }
                                  }
                              } );

                              attributePath = 'reference.srcItemId';
                              dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectAttributes } as unknown as DataObjectProperties );
                              dataDescriptorAliases = [];

                              if ( dataDescriptor?.alias ) {
                                  dataDescriptorAliases.push( dataDescriptor.alias );
                                  this.nodeKeyToDataDescriptorAliasesRepresenting[ nodeData.key ].push( dataDescriptor.alias );
                              }
                              if ( dataDescriptor?.fullAlias ) {
                                  dataDescriptorAliases = dataDescriptorAliases.concat( dataDescriptor?.fullAlias.split( ',' ) );
                              }

                              nodeData.data.columnDefinition.push( {
                                  type               : 'referenceMeta',
                                  key                : 'srcItemId',
                                  label              : __( 'YN.base.common.attribute.srcItemId' ),
                                  icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'IntegerField' )?.icon}`,
                                  dataDescriptorAlias: dataDescriptorAliases,
                                  data               : {
                                      itemTypeName: 'Column',
                                      key         : `${payloadConfig.referenceSet.shortName}:srcItemId`,
                                      name        : `${payloadConfig.referenceSet.shortName} srcItemId`,
                                      dataObjectId: dataObjectAttributes.id,
                                      properties  : {
                                          AttributePath: attributePath
                                      }
                                  }
                              } );

                              attributePath = 'reference.destItemId';
                              dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectAttributes } as unknown as DataObjectProperties );
                              dataDescriptorAliases = [];

                              if ( dataDescriptor?.alias ) {
                                  dataDescriptorAliases.push( dataDescriptor.alias );
                                  this.nodeKeyToDataDescriptorAliasesRepresenting[ nodeData.key ].push( dataDescriptor.alias );
                              }
                              if ( dataDescriptor?.fullAlias ) {
                                  dataDescriptorAliases = dataDescriptorAliases.concat( dataDescriptor?.fullAlias.split( ',' ) );
                              }

                              nodeData.data.columnDefinition.push( {
                                  type               : 'referenceMeta',
                                  key                : 'destItemId',
                                  label              : __( 'YN.base.common.attribute.destItemId' ),
                                  icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'IntegerField' )?.icon}`,
                                  dataDescriptorAlias: dataDescriptorAliases,
                                  data               : {
                                      itemTypeName: 'Column',
                                      key         : `${payloadConfig.referenceSet.shortName}:destItemId`,
                                      name        : `${payloadConfig.referenceSet.shortName} destItemId`,
                                      dataObjectId: dataObjectAttributes.id,
                                      properties  : {
                                          AttributePath: attributePath
                                      }
                                  }
                              } );

                              attributePath = 'reference.modifierUserId';
                              dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectAttributes } as unknown as DataObjectProperties );
                              dataDescriptorAliases = [];

                              if ( dataDescriptor?.alias ) {
                                  dataDescriptorAliases.push( dataDescriptor.alias );
                                  this.nodeKeyToDataDescriptorAliasesRepresenting[ nodeData.key ].push( dataDescriptor.alias );
                              }
                              if ( dataDescriptor?.fullAlias ) {
                                  dataDescriptorAliases = dataDescriptorAliases.concat( dataDescriptor?.fullAlias.split( ',' ) );
                              }

                              nodeData.data.columnDefinition.push( {
                                  type               : 'referenceMeta',
                                  key                : 'modifierUserId',
                                  label              : __( 'YN.base.common.attribute.modifierUserId' ),
                                  icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'StringField' )?.icon}`,
                                  dataDescriptorAlias: dataDescriptorAliases,
                                  data               : {
                                      itemTypeName: 'Column',
                                      key         : `${payloadConfig.referenceSet.shortName}:modifierUserId`,
                                      name        : `${payloadConfig.referenceSet.shortName} modifierUserId`,
                                      dataObjectId: dataObjectAttributes.id,
                                      properties  : {
                                          AttributePath: attributePath
                                      }
                                  }
                              } );

                              attributePath = 'reference.modificationTime';
                              dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectAttributes } as unknown as DataObjectProperties );
                              dataDescriptorAliases = [];

                              if ( dataDescriptor?.alias ) {
                                  dataDescriptorAliases.push( dataDescriptor.alias );
                                  this.nodeKeyToDataDescriptorAliasesRepresenting[ nodeData.key ].push( dataDescriptor.alias );
                              }
                              if ( dataDescriptor?.fullAlias ) {
                                  dataDescriptorAliases = dataDescriptorAliases.concat( dataDescriptor?.fullAlias.split( ',' ) );
                              }

                              nodeData.data.columnDefinition.push( {
                                  type               : 'referenceMeta',
                                  key                : 'modificationTime',
                                  label              : __( 'YN.base.common.attribute.modificationTime' ),
                                  icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'TimestampField' )?.icon}`,
                                  dataDescriptorAlias: dataDescriptorAliases,
                                  data               : {
                                      itemTypeName: 'Column',
                                      key         : `${payloadConfig.referenceSet.shortName}:modificationTime`,
                                      name        : `${payloadConfig.referenceSet.shortName} modificationTime`,
                                      dataObjectId: dataObjectAttributes.id,
                                      properties  : {
                                          AttributePath: attributePath
                                      }
                                  }
                              } );

                              attributePath = 'reference.validatorUserId';
                              dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectAttributes } as unknown as DataObjectProperties );
                              dataDescriptorAliases = [];

                              if ( dataDescriptor?.alias ) {
                                  dataDescriptorAliases.push( dataDescriptor.alias );
                                  this.nodeKeyToDataDescriptorAliasesRepresenting[ nodeData.key ].push( dataDescriptor.alias );
                              }
                              if ( dataDescriptor?.fullAlias ) {
                                  dataDescriptorAliases = dataDescriptorAliases.concat( dataDescriptor?.fullAlias.split( ',' ) );
                              }

                              nodeData.data.columnDefinition.push( {
                                  type               : 'referenceMeta',
                                  key                : 'validatorUserId',
                                  label              : __( 'YN.base.common.attribute.validatorUserId' ),
                                  icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'StringField' )?.icon}`,
                                  dataDescriptorAlias: dataDescriptorAliases,
                                  data               : {
                                      itemTypeName: 'Column',
                                      key         : `${payloadConfig.referenceSet.shortName}:validatorUserId`,
                                      name        : `${payloadConfig.referenceSet.shortName} validatorUserId`,
                                      dataObjectId: dataObjectAttributes.id,
                                      properties  : {
                                          AttributePath: attributePath
                                      }
                                  }
                              } );

                              attributePath = 'reference.validationTime';
                              dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectAttributes } as unknown as DataObjectProperties );
                              dataDescriptorAliases = [];

                              if ( dataDescriptor?.alias ) {
                                  dataDescriptorAliases.push( dataDescriptor.alias );
                                  this.nodeKeyToDataDescriptorAliasesRepresenting[ nodeData.key ].push( dataDescriptor.alias );
                              }
                              if ( dataDescriptor?.fullAlias ) {
                                  dataDescriptorAliases = dataDescriptorAliases.concat( dataDescriptor?.fullAlias.split( ',' ) );
                              }

                              nodeData.data.columnDefinition.push( {
                                  type               : 'referenceMeta',
                                  key                : 'validationTime',
                                  label              : __( 'YN.base.common.attribute.validationTime' ),
                                  icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'TimestampField' )?.icon}`,
                                  dataDescriptorAlias: dataDescriptorAliases,
                                  data               : {
                                      itemTypeName: 'Column',
                                      key         : `${payloadConfig.referenceSet.shortName}:validationTime`,
                                      name        : `${payloadConfig.referenceSet.shortName} validationTime`,
                                      dataObjectId: dataObjectAttributes.id,
                                      properties  : {
                                          AttributePath: attributePath
                                      }
                                  }
                              } );


                              // reference properties - get possible fields for all properties
                              const referencePropertySets = metaModel[ schema ].getReferencePropertySetsForReferenceSet( payloadConfig.referenceSet.id );

                              referencePropertySets.sort(
                                  ( pset1, pset2 ) => pset1.label.toLowerCase() > pset2.label.toLowerCase() ? 1 : ( pset1.label.toLowerCase() < pset2.label.toLowerCase() ? -1 : 0 )
                              );

                              for ( const referencePropertySet of referencePropertySets ) {
                                  let key  = `${payloadConfig.referenceSet.shortName}:${referencePropertySet.name}`,
                                      name = `${payloadConfig.referenceSet.shortName} ${referencePropertySet.name}`;

                                  attributePath = `referenceProperty.${referencePropertySet.name}`;
                                  dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectAttributes } as unknown as DataObjectProperties );
                                  dataDescriptorAliases = [];

                                  if ( dataDescriptor?.alias ) {
                                      dataDescriptorAliases.push( dataDescriptor.alias );
                                      this.nodeKeyToDataDescriptorAliasesRepresenting[ nodeData.key ].push( dataDescriptor.alias );
                                  }
                                  if ( dataDescriptor?.fullAlias ) {
                                      dataDescriptorAliases = dataDescriptorAliases.concat( dataDescriptor?.fullAlias.split( ',' ) );
                                  }

                                  if ( referencePropertySet.isEnumerated ) {
                                      // TODO: single ChartDataSets for key, value and key & value?

                                      if ( referencePropertySet.isKeySettable && referencePropertySet.isValueSettable ) {
                                          key += ':key_value';
                                          name += ' key/value';
                                          attributePath = `${attributePath}.key + ': ' + ${attributePath}.value`;
                                      }
                                      else if ( referencePropertySet.isKeySettable ) {
                                          key += ':key';
                                          name += ' key';
                                          attributePath = `${attributePath}.key`;
                                      }
                                      else {
                                          key += ':value';
                                          name += ' value';
                                          attributePath = `${attributePath}.value`;
                                      }

                                      nodeData.data.columnDefinition.push( {
                                          type               : 'referenceProperty',
                                          key                : referencePropertySet.name,
                                          label              : referencePropertySet.label,
                                          icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'EnumField' )?.icon}`,
                                          dataDescriptorAlias: dataDescriptorAliases,
                                          data               : {
                                              itemTypeName: 'Column',
                                              key,
                                              name,
                                              dataObjectId: dataObjectAttributes.id,
                                              properties  : {
                                                  AttributePath: attributePath
                                              }
                                          }
                                      } );
                                  }
                                  else {
                                      name += ' value';

                                      nodeData.data.columnDefinition.push( {
                                          type               : 'referenceProperty',
                                          key                : referencePropertySet.name,
                                          label              : referencePropertySet.label,
                                          icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( fieldTypeToItemTypeMapping[ referencePropertySet.valueType ] )?.icon}`,
                                          dataDescriptorAlias: dataDescriptorAliases,
                                          data               : {
                                              itemTypeName: 'Column',
                                              key,
                                              name,
                                              dataObjectId: dataObjectAttributes.id,
                                              properties  : {
                                                  AttributePath: attributePath
                                              }
                                          }
                                      } );
                                  }


                                  // property meta
                                  attributePath = `referenceProperty.${referencePropertySet.name}.propertyTypeName`;
                                  dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectAttributes } as unknown as DataObjectProperties );
                                  dataDescriptorAliases = [];

                                  if ( dataDescriptor?.alias ) {
                                      dataDescriptorAliases.push( dataDescriptor.alias );
                                      this.nodeKeyToDataDescriptorAliasesRepresenting[ nodeData.key ].push( dataDescriptor.alias );
                                  }
                                  if ( dataDescriptor?.fullAlias ) {
                                      dataDescriptorAliases = dataDescriptorAliases.concat( dataDescriptor?.fullAlias.split( ',' ) );
                                  }

                                  nodeData.data.columnDefinition.push( {
                                      type               : 'referenceMeta',
                                      key                : 'propertyTypeName',
                                      label              : __( 'YN.base.common.attribute.propertyTypeName' ),
                                      icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'StringField' )?.icon}`,
                                      dataDescriptorAlias: dataDescriptorAliases,
                                      data               : {
                                          itemTypeName: 'Column',
                                          key         : `${currentItemTypeName}:${referencePropertySet.name}:propertyTypeName`,
                                          name        : `${currentItemTypeName}:${referencePropertySet.name} propertyTypeName`,
                                          dataObjectId: dataObjectAttributes.id,
                                          properties  : {
                                              AttributePath: attributePath
                                          }
                                      }
                                  } );

                                  attributePath = `referenceProperty.${referencePropertySet.name}.referencePropertySetId`;
                                  dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectAttributes } as unknown as DataObjectProperties );
                                  dataDescriptorAliases = [];

                                  if ( dataDescriptor?.alias ) {
                                      dataDescriptorAliases.push( dataDescriptor.alias );
                                      this.nodeKeyToDataDescriptorAliasesRepresenting[ nodeData.key ].push( dataDescriptor.alias );
                                  }
                                  if ( dataDescriptor?.fullAlias ) {
                                      dataDescriptorAliases = dataDescriptorAliases.concat( dataDescriptor?.fullAlias.split( ',' ) );
                                  }

                                  nodeData.data.columnDefinition.push( {
                                      type               : 'referenceMeta',
                                      key                : 'referencePropertySetId',
                                      label              : __( 'YN.base.common.attribute.referencePropertySetId' ),
                                      icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'IntegerField' )?.icon}`,
                                      dataDescriptorAlias: dataDescriptorAliases,
                                      data               : {
                                          itemTypeName: 'Column',
                                          key         : `${currentItemTypeName}:${referencePropertySet.name}:referencePropertySetId`,
                                          name        : `${currentItemTypeName}:${referencePropertySet.name} referencePropertySetId`,
                                          dataObjectId: dataObjectAttributes.id,
                                          properties  : {
                                              AttributePath: attributePath
                                          }
                                      }
                                  } );

                                  attributePath = `referenceProperty.${referencePropertySet.name}.statusId`;
                                  dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectAttributes } as unknown as DataObjectProperties );
                                  dataDescriptorAliases = [];

                                  if ( dataDescriptor?.alias ) {
                                      dataDescriptorAliases.push( dataDescriptor.alias );
                                      this.nodeKeyToDataDescriptorAliasesRepresenting[ nodeData.key ].push( dataDescriptor.alias );
                                  }
                                  if ( dataDescriptor?.fullAlias ) {
                                      dataDescriptorAliases = dataDescriptorAliases.concat( dataDescriptor?.fullAlias.split( ',' ) );
                                  }

                                  nodeData.data.columnDefinition.push( {
                                      type               : 'referenceMeta',
                                      key                : 'statusId',
                                      label              : __( 'YN.base.common.attribute.statusId' ),
                                      icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'IntegerField' )?.icon}`,
                                      dataDescriptorAlias: dataDescriptorAliases,
                                      data               : {
                                          itemTypeName: 'Column',
                                          key         : `${currentItemTypeName}:${referencePropertySet.name}:statusId`,
                                          name        : `${currentItemTypeName}:${referencePropertySet.name} statusId`,
                                          dataObjectId: dataObjectAttributes.id,
                                          properties  : {
                                              AttributePath: attributePath
                                          }
                                      }
                                  } );

                                  attributePath = `referenceProperty.${referencePropertySet.name}.modifierUserId`;
                                  dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectAttributes } as unknown as DataObjectProperties );
                                  dataDescriptorAliases = [];

                                  if ( dataDescriptor?.alias ) {
                                      dataDescriptorAliases.push( dataDescriptor.alias );
                                      this.nodeKeyToDataDescriptorAliasesRepresenting[ nodeData.key ].push( dataDescriptor.alias );
                                  }
                                  if ( dataDescriptor?.fullAlias ) {
                                      dataDescriptorAliases = dataDescriptorAliases.concat( dataDescriptor?.fullAlias.split( ',' ) );
                                  }

                                  nodeData.data.columnDefinition.push( {
                                      type               : 'referenceMeta',
                                      key                : 'modifierUserId',
                                      label              : __( 'YN.base.common.attribute.modifierUserId' ),
                                      icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'StringField' )?.icon}`,
                                      dataDescriptorAlias: dataDescriptorAliases,
                                      data               : {
                                          itemTypeName: 'Column',
                                          key         : `${currentItemTypeName}:${referencePropertySet.name}:modifierUserId`,
                                          name        : `${currentItemTypeName}:${referencePropertySet.name} modifierUserId`,
                                          dataObjectId: dataObjectAttributes.id,
                                          properties  : {
                                              AttributePath: attributePath
                                          }
                                      }
                                  } );

                                  attributePath = `referenceProperty.${referencePropertySet.name}.modificationTime`;
                                  dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectAttributes } as unknown as DataObjectProperties );
                                  dataDescriptorAliases = [];

                                  if ( dataDescriptor?.alias ) {
                                      dataDescriptorAliases.push( dataDescriptor.alias );
                                      this.nodeKeyToDataDescriptorAliasesRepresenting[ nodeData.key ].push( dataDescriptor.alias );
                                  }
                                  if ( dataDescriptor?.fullAlias ) {
                                      dataDescriptorAliases = dataDescriptorAliases.concat( dataDescriptor?.fullAlias.split( ',' ) );
                                  }

                                  nodeData.data.columnDefinition.push( {
                                      type               : 'referenceMeta',
                                      key                : 'modificationTime',
                                      label              : __( 'YN.base.common.attribute.modificationTime' ),
                                      icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'TimestampField' )?.icon}`,
                                      dataDescriptorAlias: dataDescriptorAliases,
                                      data               : {
                                          itemTypeName: 'Column',
                                          key         : `${currentItemTypeName}:${referencePropertySet.name}:modificationTime`,
                                          name        : `${currentItemTypeName}:${referencePropertySet.name} modificationTime`,
                                          dataObjectId: dataObjectAttributes.id,
                                          properties  : {
                                              AttributePath: attributePath
                                          }
                                      }
                                  } );

                                  attributePath = `referenceProperty.${referencePropertySet.name}.validatorUserId`;
                                  dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectAttributes } as unknown as DataObjectProperties );
                                  dataDescriptorAliases = [];

                                  if ( dataDescriptor?.alias ) {
                                      dataDescriptorAliases.push( dataDescriptor.alias );
                                      this.nodeKeyToDataDescriptorAliasesRepresenting[ nodeData.key ].push( dataDescriptor.alias );
                                  }
                                  if ( dataDescriptor?.fullAlias ) {
                                      dataDescriptorAliases = dataDescriptorAliases.concat( dataDescriptor?.fullAlias.split( ',' ) );
                                  }

                                  nodeData.data.columnDefinition.push( {
                                      type               : 'referenceMeta',
                                      key                : 'validatorUserId',
                                      label              : __( 'YN.base.common.attribute.validatorUserId' ),
                                      icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'StringField' )?.icon}`,
                                      dataDescriptorAlias: dataDescriptorAliases,
                                      data               : {
                                          itemTypeName: 'Column',
                                          key         : `${currentItemTypeName}:${referencePropertySet.name}:validatorUserId`,
                                          name        : `${currentItemTypeName}:${referencePropertySet.name} validatorUserId`,
                                          dataObjectId: dataObjectAttributes.id,
                                          properties  : {
                                              AttributePath: attributePath
                                          }
                                      }
                                  } );

                                  attributePath = `referenceProperty.${referencePropertySet.name}.validationTime`;
                                  dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectAttributes } as unknown as DataObjectProperties );
                                  dataDescriptorAliases = [];

                                  if ( dataDescriptor?.alias ) {
                                      dataDescriptorAliases.push( dataDescriptor.alias );
                                      this.nodeKeyToDataDescriptorAliasesRepresenting[ nodeData.key ].push( dataDescriptor.alias );
                                  }
                                  if ( dataDescriptor?.fullAlias ) {
                                      dataDescriptorAliases = dataDescriptorAliases.concat( dataDescriptor?.fullAlias.split( ',' ) );
                                  }

                                  nodeData.data.columnDefinition.push( {
                                      type               : 'referenceMeta',
                                      key                : 'validationTime',
                                      label              : __( 'YN.base.common.attribute.validationTime' ),
                                      icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'TimestampField' )?.icon}`,
                                      dataDescriptorAlias: dataDescriptorAliases,
                                      data               : {
                                          itemTypeName: 'Column',
                                          key         : `${currentItemTypeName}:${referencePropertySet.name}:validationTime`,
                                          name        : `${currentItemTypeName}:${referencePropertySet.name} validationTime`,
                                          dataObjectId: dataObjectAttributes.id,
                                          properties  : {
                                              AttributePath: attributePath
                                          }
                                      }
                                  } );
                              }
                          }


                          // image properties - if ItemType has image properties -> special columnDefinition for display as image
                          for ( const propertySet of propertySets ) {
                              if ( propertySet.valueType === 'IMAGEDATA' ) {
                                  attributePath = 'collect( \'imageData( "key": "\' + property.Content.key + \'", "value": "\' + property.Content.value + \'" )\' ) + ( \'cssClass(\' + property.CssClass + \')\' )';

                                  dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectAttributes } as unknown as DataObjectProperties );
                                  dataDescriptorAliases = [];

                                  if ( dataDescriptor?.alias ) {
                                      dataDescriptorAliases.push( dataDescriptor.alias );
                                      this.nodeKeyToDataDescriptorAliasesRepresenting[ nodeData.key ].push( dataDescriptor.alias );
                                  }
                                  if ( dataDescriptor?.fullAlias ) {
                                      dataDescriptorAliases = dataDescriptorAliases.concat( dataDescriptor?.fullAlias.split( ',' ) );
                                  }

                                  nodeData.data.columnDefinition.push( {
                                      type               : 'image',
                                      key                : 'image',
                                      label              : __( 'YN.base.frontend.overviewDesigner.showAsImage' ),
                                      icon               : 'yn-icon icon-itemtype-detail pi pi-image',
                                      dataDescriptorAlias: dataDescriptorAliases,
                                      data               : {
                                          itemTypeName  : 'Column',
                                          key           : `${currentItemTypeName}:image`,
                                          name          : `${currentItemTypeName} image`,
                                          dataObjectId  : dataObjectAttributes.id,
                                          properties    : {
                                              AttributePath: attributePath
                                          },
                                          additionalData: [
                                              {
                                                  itemTypeName      : 'Format',
                                                  key               : 'Format',
                                                  name              : 'Format',
                                                  referenceTypeName : 'isFormatFor',
                                                  referenceDirection: 'src',
                                                  properties        : {
                                                      Type: 'Image'
                                                  }
                                              }
                                          ]
                                      }
                                  } );

                                  break;
                              }
                          }


                          // item attributes - id, key, name
                          attributePath = 'item.id';
                          dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectAttributes } as unknown as DataObjectProperties );
                          dataDescriptorAliases = [];

                          if ( dataDescriptor?.alias ) {
                              dataDescriptorAliases.push( dataDescriptor.alias );
                              this.nodeKeyToDataDescriptorAliasesRepresenting[ nodeData.key ].push( dataDescriptor.alias );
                          }
                          if ( dataDescriptor?.fullAlias ) {
                              dataDescriptorAliases = dataDescriptorAliases.concat( dataDescriptor?.fullAlias.split( ',' ) );
                          }

                          nodeData.data.columnDefinition.push( {
                              type               : 'item',
                              key                : 'id',
                              label              : __( 'YN.base.common.attribute.id' ),
                              icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'IntegerField' )?.icon}`,
                              dataDescriptorAlias: dataDescriptorAliases,
                              data               : {
                                  itemTypeName: 'Column',
                                  key         : `${currentItemTypeName}:id`,
                                  name        : `${currentItemTypeName} id`,
                                  dataObjectId: dataObjectAttributes.id,
                                  properties  : {
                                      AttributePath: attributePath
                                  }
                              }
                          } );

                          attributePath = 'item.key';
                          dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectAttributes } as unknown as DataObjectProperties );
                          dataDescriptorAliases = [];

                          if ( dataDescriptor?.alias ) {
                              dataDescriptorAliases.push( dataDescriptor.alias );
                              this.nodeKeyToDataDescriptorAliasesRepresenting[ nodeData.key ].push( dataDescriptor.alias );
                          }
                          if ( dataDescriptor?.fullAlias ) {
                              dataDescriptorAliases = dataDescriptorAliases.concat( dataDescriptor?.fullAlias.split( ',' ) );
                          }

                          nodeData.data.columnDefinition.push( {
                              type               : 'item',
                              key                : 'key',
                              label              : getItemKeyDisplayName( dataObjectAttributes.objectType, schema ),
                              icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'StringField' )?.icon}`,
                              dataDescriptorAlias: dataDescriptorAliases,
                              data               : {
                                  itemTypeName: 'Column',
                                  key         : `${currentItemTypeName}:key`,
                                  name        : `${currentItemTypeName} key`,
                                  dataObjectId: dataObjectAttributes.id,
                                  properties  : {
                                      AttributePath: attributePath
                                  }
                              }
                          } );

                          attributePath = 'item.name';
                          dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectAttributes } as unknown as DataObjectProperties );
                          dataDescriptorAliases = [];

                          if ( dataDescriptor?.alias ) {
                              dataDescriptorAliases.push( dataDescriptor.alias );
                              this.nodeKeyToDataDescriptorAliasesRepresenting[ nodeData.key ].push( dataDescriptor.alias );
                          }
                          if ( dataDescriptor?.fullAlias ) {
                              dataDescriptorAliases = dataDescriptorAliases.concat( dataDescriptor?.fullAlias.split( ',' ) );
                          }

                          nodeData.data.columnDefinition.push( {
                              type               : 'item',
                              key                : 'name',
                              label              : getItemNameDisplayName( dataObjectAttributes.objectType, schema ),
                              icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'StringField' )?.icon}`,
                              dataDescriptorAlias: dataDescriptorAliases,
                              data               : {
                                  itemTypeName: 'Column',
                                  key         : `${currentItemTypeName}:name`,
                                  name        : `${currentItemTypeName} name`,
                                  dataObjectId: dataObjectAttributes.id,
                                  properties  : {
                                      AttributePath: attributePath
                                  }
                              }
                          } );


                          // item meta
                          attributePath = 'item.itemTypeName';
                          dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectAttributes } as unknown as DataObjectProperties );
                          dataDescriptorAliases = [];

                          if ( dataDescriptor?.alias ) {
                              dataDescriptorAliases.push( dataDescriptor.alias );
                              this.nodeKeyToDataDescriptorAliasesRepresenting[ nodeData.key ].push( dataDescriptor.alias );
                          }
                          if ( dataDescriptor?.fullAlias ) {
                              dataDescriptorAliases = dataDescriptorAliases.concat( dataDescriptor?.fullAlias.split( ',' ) );
                          }

                          nodeData.data.columnDefinition.push( {
                              type               : 'meta',
                              key                : 'itemTypeName',
                              label              : __( 'YN.base.common.attribute.itemTypeName' ),
                              icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'StringField' )?.icon}`,
                              dataDescriptorAlias: dataDescriptorAliases,
                              data               : {
                                  itemTypeName: 'Column',
                                  key         : `${currentItemTypeName}:itemTypeName`,
                                  name        : `${currentItemTypeName} itemTypeName`,
                                  dataObjectId: dataObjectAttributes.id,
                                  properties  : {
                                      AttributePath: attributePath
                                  }
                              }
                          } );

                          attributePath = 'item.itemTypeId';
                          dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectAttributes } as unknown as DataObjectProperties );
                          dataDescriptorAliases = [];

                          if ( dataDescriptor?.alias ) {
                              dataDescriptorAliases.push( dataDescriptor.alias );
                              this.nodeKeyToDataDescriptorAliasesRepresenting[ nodeData.key ].push( dataDescriptor.alias );
                          }
                          if ( dataDescriptor?.fullAlias ) {
                              dataDescriptorAliases = dataDescriptorAliases.concat( dataDescriptor?.fullAlias.split( ',' ) );
                          }

                          nodeData.data.columnDefinition.push( {
                              type               : 'meta',
                              key                : 'itemTypeId',
                              label              : __( 'YN.base.common.attribute.itemTypeId' ),
                              icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'IntegerField' )?.icon}`,
                              dataDescriptorAlias: dataDescriptorAliases,
                              data               : {
                                  itemTypeName: 'Column',
                                  key         : `${currentItemTypeName}:itemTypeId`,
                                  name        : `${currentItemTypeName} itemTypeId`,
                                  dataObjectId: dataObjectAttributes.id,
                                  properties  : {
                                      AttributePath: attributePath
                                  }
                              }
                          } );

                          attributePath = 'item.statusId';
                          dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectAttributes } as unknown as DataObjectProperties );
                          dataDescriptorAliases = [];

                          if ( dataDescriptor?.alias ) {
                              dataDescriptorAliases.push( dataDescriptor.alias );
                              this.nodeKeyToDataDescriptorAliasesRepresenting[ nodeData.key ].push( dataDescriptor.alias );
                          }
                          if ( dataDescriptor?.fullAlias ) {
                              dataDescriptorAliases = dataDescriptorAliases.concat( dataDescriptor?.fullAlias.split( ',' ) );
                          }

                          nodeData.data.columnDefinition.push( {
                              type               : 'meta',
                              key                : 'statusId',
                              label              : __( 'YN.base.common.attribute.statusId' ),
                              icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'IntegerField' )?.icon}`,
                              dataDescriptorAlias: dataDescriptorAliases,
                              data               : {
                                  itemTypeName: 'Column',
                                  key         : `${currentItemTypeName}:statusId`,
                                  name        : `${currentItemTypeName} statusId`,
                                  dataObjectId: dataObjectAttributes.id,
                                  properties  : {
                                      AttributePath: attributePath
                                  }
                              }
                          } );

                          attributePath = 'item.creatorUserId';
                          dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectAttributes } as unknown as DataObjectProperties );
                          dataDescriptorAliases = [];

                          if ( dataDescriptor?.alias ) {
                              dataDescriptorAliases.push( dataDescriptor.alias );
                              this.nodeKeyToDataDescriptorAliasesRepresenting[ nodeData.key ].push( dataDescriptor.alias );
                          }
                          if ( dataDescriptor?.fullAlias ) {
                              dataDescriptorAliases = dataDescriptorAliases.concat( dataDescriptor?.fullAlias.split( ',' ) );
                          }

                          nodeData.data.columnDefinition.push( {
                              type               : 'meta',
                              key                : 'creatorUserId',
                              label              : __( 'YN.base.common.attribute.creatorUserId' ),
                              icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'StringField' )?.icon}`,
                              dataDescriptorAlias: dataDescriptorAliases,
                              data               : {
                                  itemTypeName: 'Column',
                                  key         : `${currentItemTypeName}:creatorUserId`,
                                  name        : `${currentItemTypeName} creatorUserId`,
                                  dataObjectId: dataObjectAttributes.id,
                                  properties  : {
                                      AttributePath: attributePath
                                  }
                              }
                          } );

                          attributePath = 'item.creationTime';
                          dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectAttributes } as unknown as DataObjectProperties );
                          dataDescriptorAliases = [];

                          if ( dataDescriptor?.alias ) {
                              dataDescriptorAliases.push( dataDescriptor.alias );
                              this.nodeKeyToDataDescriptorAliasesRepresenting[ nodeData.key ].push( dataDescriptor.alias );
                          }
                          if ( dataDescriptor?.fullAlias ) {
                              dataDescriptorAliases = dataDescriptorAliases.concat( dataDescriptor?.fullAlias.split( ',' ) );
                          }

                          nodeData.data.columnDefinition.push( {
                              type               : 'meta',
                              key                : 'creationTime',
                              label              : __( 'YN.base.common.attribute.creationTime' ),
                              icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'TimestampField' )?.icon}`,
                              dataDescriptorAlias: dataDescriptorAliases,
                              data               : {
                                  itemTypeName: 'Column',
                                  key         : `${currentItemTypeName}:creationTime`,
                                  name        : `${currentItemTypeName} creationTime`,
                                  dataObjectId: dataObjectAttributes.id,
                                  properties  : {
                                      AttributePath: attributePath
                                  }
                              }
                          } );

                          attributePath = 'item.modifierUserId';
                          dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectAttributes } as unknown as DataObjectProperties );
                          dataDescriptorAliases = [];

                          if ( dataDescriptor?.alias ) {
                              dataDescriptorAliases.push( dataDescriptor.alias );
                              this.nodeKeyToDataDescriptorAliasesRepresenting[ nodeData.key ].push( dataDescriptor.alias );
                          }
                          if ( dataDescriptor?.fullAlias ) {
                              dataDescriptorAliases = dataDescriptorAliases.concat( dataDescriptor?.fullAlias.split( ',' ) );
                          }

                          nodeData.data.columnDefinition.push( {
                              type               : 'meta',
                              key                : 'modifierUserId',
                              label              : __( 'YN.base.common.attribute.modifierUserId' ),
                              icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'StringField' )?.icon}`,
                              dataDescriptorAlias: dataDescriptorAliases,
                              data               : {
                                  itemTypeName: 'Column',
                                  key         : `${currentItemTypeName}:modifierUserId`,
                                  name        : `${currentItemTypeName} modifierUserId`,
                                  dataObjectId: dataObjectAttributes.id,
                                  properties  : {
                                      AttributePath: attributePath
                                  }
                              }
                          } );

                          attributePath = 'item.modificationTime';
                          dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectAttributes } as unknown as DataObjectProperties );
                          dataDescriptorAliases = [];

                          if ( dataDescriptor?.alias ) {
                              dataDescriptorAliases.push( dataDescriptor.alias );
                              this.nodeKeyToDataDescriptorAliasesRepresenting[ nodeData.key ].push( dataDescriptor.alias );
                          }
                          if ( dataDescriptor?.fullAlias ) {
                              dataDescriptorAliases = dataDescriptorAliases.concat( dataDescriptor?.fullAlias.split( ',' ) );
                          }

                          nodeData.data.columnDefinition.push( {
                              type               : 'meta',
                              key                : 'modificationTime',
                              label              : __( 'YN.base.common.attribute.modificationTime' ),
                              icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'TimestampField' )?.icon}`,
                              dataDescriptorAlias: dataDescriptorAliases,
                              data               : {
                                  itemTypeName: 'Column',
                                  key         : `${currentItemTypeName}:modificationTime`,
                                  name        : `${currentItemTypeName} modificationTime`,
                                  dataObjectId: dataObjectAttributes.id,
                                  properties  : {
                                      AttributePath: attributePath
                                  }
                              }
                          } );

                          attributePath = 'item.validatorUserId';
                          dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectAttributes } as unknown as DataObjectProperties );
                          dataDescriptorAliases = [];

                          if ( dataDescriptor?.alias ) {
                              dataDescriptorAliases.push( dataDescriptor.alias );
                              this.nodeKeyToDataDescriptorAliasesRepresenting[ nodeData.key ].push( dataDescriptor.alias );
                          }
                          if ( dataDescriptor?.fullAlias ) {
                              dataDescriptorAliases = dataDescriptorAliases.concat( dataDescriptor?.fullAlias.split( ',' ) );
                          }

                          nodeData.data.columnDefinition.push( {
                              type               : 'meta',
                              key                : 'validatorUserId',
                              label              : __( 'YN.base.common.attribute.validatorUserId' ),
                              icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'StringField' )?.icon}`,
                              dataDescriptorAlias: dataDescriptorAliases,
                              data               : {
                                  itemTypeName: 'Column',
                                  key         : `${currentItemTypeName}:validatorUserId`,
                                  name        : `${currentItemTypeName} validatorUserId`,
                                  dataObjectId: dataObjectAttributes.id,
                                  properties  : {
                                      AttributePath: attributePath
                                  }
                              }
                          } );

                          attributePath = 'item.validationTime';
                          dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectAttributes } as unknown as DataObjectProperties );
                          dataDescriptorAliases = [];

                          if ( dataDescriptor?.alias ) {
                              dataDescriptorAliases.push( dataDescriptor.alias );
                              this.nodeKeyToDataDescriptorAliasesRepresenting[ nodeData.key ].push( dataDescriptor.alias );
                          }
                          if ( dataDescriptor?.fullAlias ) {
                              dataDescriptorAliases = dataDescriptorAliases.concat( dataDescriptor?.fullAlias.split( ',' ) );
                          }

                          nodeData.data.columnDefinition.push( {
                              type               : 'meta',
                              key                : 'validationTime',
                              label              : __( 'YN.base.common.attribute.validationTime' ),
                              icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'TimestampField' )?.icon}`,
                              dataDescriptorAlias: dataDescriptorAliases,
                              data               : {
                                  itemTypeName: 'Column',
                                  key         : `${currentItemTypeName}:validationTime`,
                                  name        : `${currentItemTypeName} validationTime`,
                                  dataObjectId: dataObjectAttributes.id,
                                  properties  : {
                                      AttributePath: attributePath
                                  }
                              }
                          } );


                          // item properties - get possible fields for all properties
                          for ( const propertySet of propertySets ) {
                              let key  = `${currentItemTypeName}:${propertySet.name}`,
                                  name = `${currentItemTypeName} ${propertySet.name}`;

                              attributePath = `property.${propertySet.name}`;
                              dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectAttributes } as unknown as DataObjectProperties );
                              dataDescriptorAliases = [];

                              if ( dataDescriptor?.alias ) {
                                  dataDescriptorAliases.push( dataDescriptor.alias );
                                  this.nodeKeyToDataDescriptorAliasesRepresenting[ nodeData.key ].push( dataDescriptor.alias );
                              }
                              if ( dataDescriptor?.fullAlias ) {
                                  dataDescriptorAliases = dataDescriptorAliases.concat( dataDescriptor?.fullAlias.split( ',' ) );
                              }

                              if ( propertySet.isEnumerated ) {
                                  // TODO: single ChartDataSets for key, value and key & value?

                                  if ( propertySet.isKeySettable && propertySet.isValueSettable ) {
                                      key += ':key_value';
                                      name += ' key/value';
                                      attributePath = `${attributePath}.key + ': ' + ${attributePath}.value`;
                                  }
                                  else if ( propertySet.isKeySettable ) {
                                      key += ':key';
                                      name += ' key';
                                      attributePath = `${attributePath}.key`;
                                  }
                                  else {
                                      key += ':value';
                                      name += ' value';
                                      attributePath = `${attributePath}.value`;
                                  }

                                  nodeData.data.columnDefinition.push( {
                                      type               : 'property',
                                      key                : propertySet.name,
                                      label              : propertySet.label,
                                      icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'EnumField' )?.icon}`,
                                      dataDescriptorAlias: dataDescriptorAliases,
                                      data               : {
                                          itemTypeName: 'Column',
                                          key,
                                          name,
                                          dataObjectId: dataObjectAttributes.id,
                                          properties  : {
                                              AttributePath: attributePath
                                          }
                                      }
                                  } );
                              }
                              else {
                                  name += ' value';

                                  nodeData.data.columnDefinition.push( {
                                      type               : 'property',
                                      key                : propertySet.name,
                                      label              : propertySet.label,
                                      icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( fieldTypeToItemTypeMapping[ propertySet.valueType ] )?.icon}`,
                                      dataDescriptorAlias: dataDescriptorAliases,
                                      data               : {
                                          itemTypeName: 'Column',
                                          key,
                                          name,
                                          dataObjectId: dataObjectAttributes.id,
                                          properties  : {
                                              AttributePath: attributePath
                                          }
                                      }
                                  } );
                              }


                              // property meta
                              attributePath = `property.${propertySet.name}.propertyTypeName`;
                              dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectAttributes } as unknown as DataObjectProperties );
                              dataDescriptorAliases = [];

                              if ( dataDescriptor?.alias ) {
                                  dataDescriptorAliases.push( dataDescriptor.alias );
                                  this.nodeKeyToDataDescriptorAliasesRepresenting[ nodeData.key ].push( dataDescriptor.alias );
                              }
                              if ( dataDescriptor?.fullAlias ) {
                                  dataDescriptorAliases = dataDescriptorAliases.concat( dataDescriptor?.fullAlias.split( ',' ) );
                              }

                              nodeData.data.columnDefinition.push( {
                                  type               : 'meta',
                                  key                : 'propertyTypeName',
                                  label              : __( 'YN.base.common.attribute.propertyTypeName' ),
                                  icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'StringField' )?.icon}`,
                                  dataDescriptorAlias: dataDescriptorAliases,
                                  data               : {
                                      itemTypeName: 'Column',
                                      key         : `${currentItemTypeName}:${propertySet.name}:propertyTypeName`,
                                      name        : `${currentItemTypeName}:${propertySet.name} propertyTypeName`,
                                      dataObjectId: dataObjectAttributes.id,
                                      properties  : {
                                          AttributePath: attributePath
                                      }
                                  }
                              } );

                              attributePath = `property.${propertySet.name}.propertySetId`;
                              dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectAttributes } as unknown as DataObjectProperties );
                              dataDescriptorAliases = [];

                              if ( dataDescriptor?.alias ) {
                                  dataDescriptorAliases.push( dataDescriptor.alias );
                                  this.nodeKeyToDataDescriptorAliasesRepresenting[ nodeData.key ].push( dataDescriptor.alias );
                              }
                              if ( dataDescriptor?.fullAlias ) {
                                  dataDescriptorAliases = dataDescriptorAliases.concat( dataDescriptor?.fullAlias.split( ',' ) );
                              }

                              nodeData.data.columnDefinition.push( {
                                  type               : 'meta',
                                  key                : 'propertySetId',
                                  label              : __( 'YN.base.common.attribute.propertySetId' ),
                                  icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'IntegerField' )?.icon}`,
                                  dataDescriptorAlias: dataDescriptorAliases,
                                  data               : {
                                      itemTypeName: 'Column',
                                      key         : `${currentItemTypeName}:${propertySet.name}:propertySetId`,
                                      name        : `${currentItemTypeName}:${propertySet.name} propertySetId`,
                                      dataObjectId: dataObjectAttributes.id,
                                      properties  : {
                                          AttributePath: attributePath
                                      }
                                  }
                              } );

                              attributePath = `property.${propertySet.name}.statusId`;
                              dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectAttributes } as unknown as DataObjectProperties );
                              dataDescriptorAliases = [];

                              if ( dataDescriptor?.alias ) {
                                  dataDescriptorAliases.push( dataDescriptor.alias );
                                  this.nodeKeyToDataDescriptorAliasesRepresenting[ nodeData.key ].push( dataDescriptor.alias );
                              }
                              if ( dataDescriptor?.fullAlias ) {
                                  dataDescriptorAliases = dataDescriptorAliases.concat( dataDescriptor?.fullAlias.split( ',' ) );
                              }

                              nodeData.data.columnDefinition.push( {
                                  type               : 'meta',
                                  key                : 'statusId',
                                  label              : __( 'YN.base.common.attribute.statusId' ),
                                  icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'IntegerField' )?.icon}`,
                                  dataDescriptorAlias: dataDescriptorAliases,
                                  data               : {
                                      itemTypeName: 'Column',
                                      key         : `${currentItemTypeName}:${propertySet.name}:statusId`,
                                      name        : `${currentItemTypeName}:${propertySet.name} statusId`,
                                      dataObjectId: dataObjectAttributes.id,
                                      properties  : {
                                          AttributePath: attributePath
                                      }
                                  }
                              } );

                              attributePath = `property.${propertySet.name}.modifierUserId`;
                              dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectAttributes } as unknown as DataObjectProperties );
                              dataDescriptorAliases = [];

                              if ( dataDescriptor?.alias ) {
                                  dataDescriptorAliases.push( dataDescriptor.alias );
                                  this.nodeKeyToDataDescriptorAliasesRepresenting[ nodeData.key ].push( dataDescriptor.alias );
                              }
                              if ( dataDescriptor?.fullAlias ) {
                                  dataDescriptorAliases = dataDescriptorAliases.concat( dataDescriptor?.fullAlias.split( ',' ) );
                              }

                              nodeData.data.columnDefinition.push( {
                                  type               : 'meta',
                                  key                : 'modifierUserId',
                                  label              : __( 'YN.base.common.attribute.modifierUserId' ),
                                  icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'StringField' )?.icon}`,
                                  dataDescriptorAlias: dataDescriptorAliases,
                                  data               : {
                                      itemTypeName: 'Column',
                                      key         : `${currentItemTypeName}:${propertySet.name}:modifierUserId`,
                                      name        : `${currentItemTypeName}:${propertySet.name} modifierUserId`,
                                      dataObjectId: dataObjectAttributes.id,
                                      properties  : {
                                          AttributePath: attributePath
                                      }
                                  }
                              } );

                              attributePath = `property.${propertySet.name}.modificationTime`;
                              dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectAttributes } as unknown as DataObjectProperties );
                              dataDescriptorAliases = [];

                              if ( dataDescriptor?.alias ) {
                                  dataDescriptorAliases.push( dataDescriptor.alias );
                                  this.nodeKeyToDataDescriptorAliasesRepresenting[ nodeData.key ].push( dataDescriptor.alias );
                              }
                              if ( dataDescriptor?.fullAlias ) {
                                  dataDescriptorAliases = dataDescriptorAliases.concat( dataDescriptor?.fullAlias.split( ',' ) );
                              }

                              nodeData.data.columnDefinition.push( {
                                  type               : 'meta',
                                  key                : 'modificationTime',
                                  label              : __( 'YN.base.common.attribute.modificationTime' ),
                                  icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'TimestampField' )?.icon}`,
                                  dataDescriptorAlias: dataDescriptorAliases,
                                  data               : {
                                      itemTypeName: 'Column',
                                      key         : `${currentItemTypeName}:${propertySet.name}:modificationTime`,
                                      name        : `${currentItemTypeName}:${propertySet.name} modificationTime`,
                                      dataObjectId: dataObjectAttributes.id,
                                      properties  : {
                                          AttributePath: attributePath
                                      }
                                  }
                              } );

                              attributePath = `property.${propertySet.name}.validatorUserId`;
                              dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectAttributes } as unknown as DataObjectProperties );
                              dataDescriptorAliases = [];

                              if ( dataDescriptor?.alias ) {
                                  dataDescriptorAliases.push( dataDescriptor.alias );
                                  this.nodeKeyToDataDescriptorAliasesRepresenting[ nodeData.key ].push( dataDescriptor.alias );
                              }
                              if ( dataDescriptor?.fullAlias ) {
                                  dataDescriptorAliases = dataDescriptorAliases.concat( dataDescriptor?.fullAlias.split( ',' ) );
                              }

                              nodeData.data.columnDefinition.push( {
                                  type               : 'meta',
                                  key                : 'validatorUserId',
                                  label              : __( 'YN.base.common.attribute.validatorUserId' ),
                                  icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'StringField' )?.icon}`,
                                  dataDescriptorAlias: dataDescriptorAliases,
                                  data               : {
                                      itemTypeName: 'Column',
                                      key         : `${currentItemTypeName}:${propertySet.name}:validatorUserId`,
                                      name        : `${currentItemTypeName}:${propertySet.name} validatorUserId`,
                                      dataObjectId: dataObjectAttributes.id,
                                      properties  : {
                                          AttributePath: attributePath
                                      }
                                  }
                              } );

                              attributePath = `property.${propertySet.name}.validationTime`;
                              dataDescriptor = buildDataDescriptor( { attributePath }, { item: dataObjectAttributes } as unknown as DataObjectProperties );
                              dataDescriptorAliases = [];

                              if ( dataDescriptor?.alias ) {
                                  dataDescriptorAliases.push( dataDescriptor.alias );
                                  this.nodeKeyToDataDescriptorAliasesRepresenting[ nodeData.key ].push( dataDescriptor.alias );
                              }
                              if ( dataDescriptor?.fullAlias ) {
                                  dataDescriptorAliases = dataDescriptorAliases.concat( dataDescriptor?.fullAlias.split( ',' ) );
                              }

                              nodeData.data.columnDefinition.push( {
                                  type               : 'meta',
                                  key                : 'validationTime',
                                  label              : __( 'YN.base.common.attribute.validationTime' ),
                                  icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'TimestampField' )?.icon}`,
                                  dataDescriptorAlias: dataDescriptorAliases,
                                  data               : {
                                      itemTypeName: 'Column',
                                      key         : `${currentItemTypeName}:${propertySet.name}:validationTime`,
                                      name        : `${currentItemTypeName}:${propertySet.name} validationTime`,
                                      dataObjectId: dataObjectAttributes.id,
                                      properties  : {
                                          AttributePath: attributePath
                                      }
                                  }
                              } );
                          }
                      }
                  }

                  if ( nodeData.children ) {
                      for ( const childNodeData of nodeData.children ) {
                          processDynamicElementsRecursive( childNodeData as ExtendedTreeNodeData );
                      }

                      // sort by label
                      nodeData.children.sort(
                          ( childNodeData1, childNodeData2 ) => {
                              if ( ( childNodeData1.label ?? '' ) > ( childNodeData2.label ?? '' ) ) {
                                  return 1;
                              }
                              else if ( ( childNodeData2.label ?? '' ) > ( childNodeData1.label ?? '' ) ) {
                                  return -1;
                              }
                              else {
                                  return 0;
                              }
                          }
                      )
                  }
              };

        this.nodeKeyToDataDescriptorAliasesRepresenting = {};

        if ( dynamicElements ) {
            processDynamicElementsRecursive( dynamicElements );
        }

        this.props.setParam?.( this.props.uid, 'expandedKeys', newExpandedKeys );

        if ( dynamicElements ) {
            return [ dynamicElements ];
        }
        else {
            return [] as ExtendTreeNode[];
        }
    }

    async loadData( rootItemId? : number ) : Promise<boolean> {
        const {
                  apiCommand,
                  apiParams,
                  dataContext,
                  uid,
                  schema,
                  useElusiveDataFromDataContext
              } = this.props;

        apiParams.rootItemId = apiParams.rootItemId ?? rootItemId;

        return this.props.loadData?.(
            apiCommand,
            dataContext,
            useElusiveDataFromDataContext ? dataContext : uid,
            undefined,
            schema,
            {
                params                : apiParams,
                mergeMode             : 'keepExisting' as MergeMode,
                metaDataIncludesCiData: true,
                extraDataKeys         : [ 'rootItemId', 'cypherConfig' ],
                extraMetaDataKeys     : [ 'treeData', 'dataStructure', 'cypherConfig', 'rootItemId', 'dataObjectTree', 'dataObjectMapping', 'payloadSchema', 'isStopped' ]
            },
            undefined,
            this.onLoad
        );
    }

    onLoad( responseData : any ) {
        const { dataContext } = this.props,
              rootItemId      = responseData?.data?.rootItemId ?? 0;

        const rootItem : Item | undefined = global.data[ dataContext ]?.items?.[ rootItemId ];

        if ( rootItem ) {
            this.props.setParams( this.props.parentUid, {
                treeData    : undefined,
                rootItemId  : rootItem?.id,
                rootItemKey : rootItem?.key,
                rootItemType: rootItem?.itemType.name
            } );
        }
    }

    async buildTree( props : AllProps = this.props, state : State = this.state ) {
        const startTime = this.debug ? performance.now() : 0;

        const {
                  dataContext,
                  elusiveData,
                  flatDataObjectNodeMap,
                  flatNodeMap,
                  payloadIdToDynamicNodeIdMap,
                  selectionKeys,
                  treeData,
                  user,
                  uid
              }          = props,
              {
                  nodeIdToErrorDataMap
              }          = state,
              rootItemId = elusiveData?.data?.rootItemId ?? this.props.apiParams.rootItemId;

        this.treeUtils = this.getTreeUtils( uid, dataContext, elusiveData, user );

        const newTreeData = rootItemId && global.data[ dataContext ]?.items[ rootItemId ]
            ? this.getPayloadTreeData( this.treeUtils, rootItemId, elusiveData, undefined, user )
            : undefined;

        this.treeUtils.applyErrorData( nodeIdToErrorDataMap );

        const newParams : Partial<Props> = {};

        if ( newTreeData !== treeData ) {
            newParams.treeData = newTreeData;
        }
        if ( this.treeUtils?.flatNodeMap !== flatNodeMap ) {
            newParams.flatNodeMap = this.treeUtils?.flatNodeMap;
        }
        if ( this.treeUtils?.flatDataObjectNodeMap !== flatDataObjectNodeMap ) {
            newParams.flatDataObjectNodeMap = this.treeUtils?.flatDataObjectNodeMap;
        }
        if ( this.treeUtils?.payloadIdToDynamicNodeIdMap !== payloadIdToDynamicNodeIdMap ) {
            newParams.payloadIdToDynamicNodeIdMap = this.treeUtils?.payloadIdToDynamicNodeIdMap;
        }
        if ( selectionKeys === undefined && newTreeData?.[ 0 ]?.key ) {
            newParams.selectionKeys = [ newTreeData[ 0 ].key as string ];
        }

        if ( !newParams.selectionKeys && selectionKeys?.length && this.treeUtils ) {
            newParams.selectionKeys = [];

            for ( const key of selectionKeys ) {
                if ( this.treeUtils.getNodeById( key ) ) {
                    newParams.selectionKeys.push( key );
                }
                else if ( elusiveData?.selectionKeysToPath?.[ key ] ) {
                    for ( const node of Object.values( this.treeUtils.flatDataObjectNodeMap as NodeMap ) ) {
                        if ( ( node.data?.payloadIdPath as string )?.split( /-(-?\d+)/ ).join( '-' ) === ( elusiveData?.selectionKeysToPath?.[ key ] as string[] ).map( id => elusiveData?.newItemIdMapping?.[ id ] ?? id ).join( '-' ) ) {
                            newParams.selectionKeys.push( node.key as string );
                        }
                    }
                }
            }
        }

        if ( Object.keys( newParams ).length ) {
            this.props.setParams( uid, newParams );
        }

        if ( this.debug ) {
            console.log( `buildTree - ${Math.round( 1000 * ( performance.now() - startTime ) ) / 1000} ms`.asLogLine( 50 ) );
        }
    }

    setScrollPosition() {
        if ( this.doSetScrollPosition ) {
            const { parentUid } = this.props;

            let scrollElement = document.getElementById( `${parentUid}_menuContentSplitter` )?.firstChild as HTMLElement | undefined;
            if ( scrollElement && !scrollElement.style?.overflowY ) {
                scrollElement = scrollElement.firstChild as HTMLElement | undefined;
            }

            if ( scrollElement ) {
                scrollElement.scrollTop = this.props.scrollTop;
                scrollElement.scrollLeft = this.props.scrollLeft;

                if ( ( this.props.scrollTop === 0 || scrollElement.scrollTop > 0 ) && ( this.props.scrollLeft === 0 || scrollElement.scrollLeft > 0 ) ) {
                    this.doSetScrollPosition = false;
                }
            }
        }
    }

    getScrollPosition() {
        if ( !this.doSetScrollPosition ) {
            const { uid, parentUid } = this.props;

            let scrollElement = document.getElementById( `${parentUid}_menuContentSplitter` )?.firstChild as HTMLElement | undefined;
            if ( scrollElement && !scrollElement.style?.overflowY ) {
                scrollElement = scrollElement.firstChild as HTMLElement | undefined;
            }

            if ( scrollElement ) {
                this.props.setParam( uid, 'scrollTop', scrollElement.scrollTop ?? 0 );
                this.props.setParam( uid, 'scrollLeft', scrollElement.scrollLeft ?? 0 );
            }
        }
    }

    onKeyDown( event : KeyboardEvent ) {
        const key = ( ( event.target as HTMLElement )?.firstChild as HTMLElement )?.id;

        if ( key && keyboardUtils.cmdKey( event ) && [ 'KeyC', 'KeyX', 'KeyV', 'KeyD', 'KeyO' ].includes( event.code ) ) {
            const selectedNode = this.treeUtils?.getNodeById( key as string );

            // if selected is a payload node
            if ( selectedNode?.data.payloadId ) {
                switch ( event.code ) {
                    // command/ctrl-O
                    case 'KeyO':
                        this.openNewPanel( event, key as string )
                        break;
                }
            }
        }
    }

    /**
     * Checks if there are intercom messages present and shows them if.
     *
     * @private
     */
    checkForIntercomMessages( props : AllProps = this.props ) {
        // check for toasts
        if ( props.intercomMessages?.notification?.reload ) {

            void this.loadData( props.intercomMessages?.notification?.reload );

            // clear message
            this.props.unsetMessage?.( this.props.uid, 'notification', 'reload' );
        }
    }

    checkQuery() {
        const {
                  elusiveData,
                  dataContext,
                  schema,
                  uid,
                  apiParams
              }     = this.props,
              query = apiParams.rootItemId ? overviewUtilities.getQuery( dataContext, apiParams.rootItemId as number ) : undefined;

        if ( query ) {
            if ( JSON.stringify( query ) !== JSON.stringify( elusiveData?.query ) ) {
                this.props.setElusiveData( uid, { query: query } );
            }

            if ( query.type !== 'DataStructure' ) {
                if ( query.query !== elusiveData?.query?.query ) {
                    const doLoad = async () => {
                        await this.props.loadData?.(
                            'overview/loadColumnDataDescriptors',
                            dataContext,
                            uid,
                            'loadColumnDataDescriptors',
                            schema,
                            {
                                params: {
                                    type      : query.type,
                                    schema    : query.schema,
                                    query     : query.query,
                                    parameters: query.parameters
                                } as LoadColumnDataDescriptorsParameters
                            },
                            undefined,
                            ( result ) => {
                                const treeData : ExtendedTreeNodeData[] = [
                                    {
                                        className   : 'yn-dataobject-node',
                                        icon        : 'yn-tree-icon fas fa-table',
                                        id          : 'queryColumns',
                                        key         : 'queryColumns',
                                        label       : __( 'YN.base.frontend.overviewDesigner.queryColumns' ),
                                        leaf        : true,
                                        openExpanded: true,
                                        data        : {
                                            ciItem             : {
                                                id  : 0,
                                                key : '',
                                                name: ''
                                            },
                                            dataDescriptorAlias: [],
                                            columnDefinition   : []
                                        }
                                    }
                                ];

                                this.nodeKeyToDataDescriptorAliasesRepresenting = {
                                    [ treeData[ 0 ].key! ]: []
                                };

                                for ( const columnDataDescriptor of result.data.columnDataDescriptors as StaticDataDescriptor[] ) {
                                    this.nodeKeyToDataDescriptorAliasesRepresenting[ treeData[ 0 ].key! ].push( columnDataDescriptor.alias );
                                    if ( !treeData[ 0 ].data.dataDescriptorAlias ) {
                                        treeData[ 0 ].data.dataDescriptorAlias = [ columnDataDescriptor.alias ];
                                    }
                                    else {
                                        treeData[ 0 ].data.dataDescriptorAlias.push( columnDataDescriptor.alias );
                                    }

                                    treeData[ 0 ].data.columnDefinition.push( {
                                        type               : 'query',
                                        key                : columnDataDescriptor.field,
                                        label              : columnDataDescriptor.field,
                                        icon               : `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( fieldTypeToItemTypeMapping[ columnDataDescriptor.valueType ] )?.icon}`,
                                        dataDescriptorAlias: [ columnDataDescriptor.alias ],
                                        data               : {
                                            itemTypeName: 'Column',
                                            key         : columnDataDescriptor.field,
                                            name        : columnDataDescriptor.field,
                                            dataObjectId: 0,
                                            properties  : {
                                                QueryFieldName: columnDataDescriptor.field
                                            }
                                        }
                                    } );
                                }

                                this.props.setParam( uid, 'selectionKeys', [ treeData[ 0 ].key! ] );
                                this.props.setParam( uid, 'treeData', treeData );
                                this.props.setElusiveData( uid, { columnDataDescriptors: result.data.columnDataDescriptors } );
                            }
                        );
                    };

                    void doLoad();
                }
            }
            else if ( elusiveData?.columnDataDescriptors ) {
                this.props.setElusiveData( uid, { columnDataDescriptors: undefined } );
            }
        }
    }

    addNode( targetNodeId : string ) {
        const targetNode         = this.treeUtils!.getNodeById( targetNodeId ),
              payloadConfig      = targetNode ? this.getPayloadConfig( targetNode ) : undefined,
              targetItemTypeName = payloadConfig?.itemTypeName,
              schema             = payloadConfig?.schema;

        if ( targetItemTypeName && schema ) {
            const allowedReferenceSetIds = targetItemTypeName ? this.getAllowedReferenceSetsForItemType( payloadConfig.itemId ) : undefined,
                  addItemDialogConfig    = this.getAddItemDialogConfig( targetItemTypeName, allowedReferenceSetIds, targetNode );

            addItemDialogConfig.allowedReferenceSetIds = allowedReferenceSetIds;

            this.setState( { nodeToMove: '', nodeToCopy: '', addItemDialogConfig } );

            return {
                ...this.state,
                nodeToMove: '',
                nodeToCopy: '',
                addItemDialogConfig
            };
        }
        else {
            return { ...this.state };
        }
    }

    /**
     * Delete existing node (set status to -1 for existing, remove unsaved nodes directly)
     * @param deleteReferenceOnly true to delete reference only
     * @param nodeId Node to delete. If not set, current node will be deleted
     */
    deleteOrUndeleteNode( deleteReferenceOnly : boolean = false, nodeId? : string, mode : 'single' | 'ancestor' = 'single', noConfirmation : boolean = false ) {

        const { uid, dataContext, useElusiveDataFromDataContext } = this.props,
              { contextMenuNode, lastContextMenuNode }            = this.state,
              deleteObjectType                                    = mode === 'single'
                  ? ( deleteReferenceOnly ? 'Reference' : 'Item' )
                  : ( deleteReferenceOnly ? 'DescendantReferences' : 'DescendantItems' ),
              nodesToDelete                                       = mode === 'single'
                  ? [ this.treeUtils!.getNodeById( nodeId ?? ( lastContextMenuNode || contextMenuNode ) ) ]
                  : this.treeUtils!.getChildNodesById( nodeId ?? ( lastContextMenuNode || contextMenuNode ) ),
              // allow undelete only for single mode
              firstPayloadId                                      = nodesToDelete[ 0 ]?.data.payloadId,
              firstPayloadItem                                    = global.data[ dataContext ]?.items[ firstPayloadId ],
              firstPayloadReferenceId                             = nodesToDelete[ 0 ]?.data.payloadReferenceId,
              firstPayloadReference                               = global.data[ dataContext ]?.references[ firstPayloadReferenceId ],
              undelete                                            = mode === 'single' && firstPayloadReference?.isDeleted(),
              undeleteObjectType                                  = undelete && firstPayloadItem?.isDeleted() ? 'Item' : 'Reference';

        const doDeleteOrUndelete = () => {

            for ( const nodeToDelete of nodesToDelete ) {

                if ( nodeToDelete ) {
                    const
                        payloadId          = nodeToDelete?.data.payloadId,
                        payloadItem        = global.data[ dataContext ]?.items[ payloadId ],
                        payloadReferenceId = nodeToDelete?.data.payloadReferenceId,
                        payloadReference   = global.data[ dataContext ]?.references[ payloadReferenceId ];

                    if ( undelete ) {
                        // delete
                        if ( payloadReferenceId ) {
                            this.props.updateReference?.( dataContext, payloadReferenceId, { statusId: payloadReference.attributesModified.get( 'statusId' ) as number }, useElusiveDataFromDataContext ? undefined : [ uid ] );
                        }
                        if ( payloadId && undeleteObjectType === 'Item' ) {
                            this.props.updateItem?.( dataContext, payloadId, { statusId: payloadItem.attributesModified.get( 'statusId' ) as number }, useElusiveDataFromDataContext ? undefined : [ uid ] );
                        }
                    }
                    else {
                        // delete
                        if ( payloadReferenceId ) {
                            this.props.deleteReference?.(
                                dataContext, payloadReferenceId, true,
                                useElusiveDataFromDataContext ? undefined : [ uid ], false );
                        }
                        if ( payloadId && !deleteReferenceOnly ) {
                            this.props.deleteItem?.(
                                dataContext, payloadId, true,
                                useElusiveDataFromDataContext ? undefined : [ uid ], false, true );
                        }

                        if ( this.props.selectionKeys?.includes( String( nodeId ) ) ) {
                            const newSelectionKeys = ( [] as string[] ).concat( this.props.selectionKeys ),
                                  index            = newSelectionKeys.findIndex( selectedKey => selectedKey === nodeId );

                            newSelectionKeys.splice( index, 1 );
                            this.props.setParam( uid, 'selectionKeys', newSelectionKeys );
                        }
                    }

                }

                // TODO: change error handling to prevent duplicate errors ( _attributesForm )
                let formPanelKey = this.props.nodeIdToPanelKeyMap?.[ nodeId ?? this.state.contextMenuNode ?? '' ];
                formPanelKey = formPanelKey ? formPanelKey.replace( '_attributesForm', '' ) : undefined;
                if ( formPanelKey ) {
                    const formPanelKeyWithoutPrefix = formPanelKey.replace( this.props.parentUid + '_', '' );
                    const panelProps = { ...getPanelProps( this.props.parentUid ) };
                    if ( panelProps ) {
                        delete panelProps.panelProps.subPanelProps[ formPanelKeyWithoutPrefix ];
                        delete panelProps.panelProps.subPanelProps[ formPanelKeyWithoutPrefix + '_attributesForm' ];
                        setPanelProps( this.props.parentUid, panelProps );
                        // delete error of form
                        const subPanelErrors = { ...panelProps.panelProps.subPanelErrors };
                        delete subPanelErrors[ formPanelKey ];
                        delete subPanelErrors[ formPanelKey + '_attributesForm' ];
                        this.props.setParam?.( this.props.parentUid, 'subPanelErrors', subPanelErrors );
                    }

                    // delete elusive data of form
                    this.props.clearElusiveData?.( formPanelKey );
                    this.props.clearElusiveData?.( formPanelKey + '_attributesForm' );
                }
            }
        };

        if ( noConfirmation ) {
            doDeleteOrUndelete();
        }
        else {
            confirmPopup( {
                target         : document.getElementById( nodeId ?? this.state.contextMenuNode ) ?? undefined,
                message        : __( `YN.base.designer.tree.${undelete ? undeleteObjectType : deleteObjectType}.${undelete ? 'un' : ''}delete.text`, { itemKey: firstPayloadItem.key, itemName: firstPayloadItem.name, itemDisplayAttributeValue: firstPayloadItem.displayAttributeValue } ),
                icon           : 'pi pi-question-circle',
                acceptLabel    : __( 'YN.base.common.yes' ),
                acceptClassName: 'p-button-success p-button-outlined',
                rejectLabel    : __( 'YN.base.common.no' ),
                rejectClassName: 'p-button-secondary p-button-outlined mr-3',
                accept         : doDeleteOrUndelete
            } ).show();
        }

    }

    openNewPanel( event : React.KeyboardEvent<Element> | React.MouseEvent<HTMLElement, MouseEvent> | KeyboardEvent, nodeId? : string ) {
        const { dataContext, schema, status } = this.props,
              node                            = this.treeUtils?.getNodeById( nodeId ?? this.state.lastContextMenuNode ),
              payloadId                       = node?.data.payloadId;

        if ( payloadId ) {
            const item = global.data[ dataContext ]?.items[ payloadId ];
            const panelConfig = user && item ? user.getPanelPropsForItemType( schema, item.itemTypeId, undefined, undefined, !!event?.altKey )[ 0 ] : {};

            this.props.openPanel?.(
                {
                    panelType: 'FinderDetail',
                    ...panelConfig,
                    schema: schema,
                    status: status,
                    //
                    rootItemId: payloadId
                },
                undefined,
                event
            );
        }
    }

    /**
     * Get tree context menu
     */
    getTreeContextMenu( nodeId : string, showOnly : false | 'add' | 'delete' = false ) : MenuItem[] {
        const retVal : MenuItem[] = [],
              {
                  uid,
                  dataContext
              }                   = this.props,
              node                = this.treeUtils?.getNodeById( nodeId ),
              itemId              = this.treeUtils?.getPayloadIdForNode( nodeId ) ?? 0,
              item                = global.data[ dataContext ]?.items[ itemId ],
              referenceId         = node?.data?.payloadReferenceId ?? 0,
              reference           = global.data[ dataContext ]?.references[ referenceId ],
              nodeIsDeleted       = reference?.isDeleted() || item?.isDeleted(),
              payloadConfig       = node ? this.getPayloadConfig( node ) : undefined,
              itemTypeName        = payloadConfig?.itemTypeName,
              schema              = payloadConfig?.schema;

        let buttonId : string,
            menuItemId : string;

        if ( itemTypeName && schema ) {
            const allowedReferenceSetIds = this.getAllowedReferenceSetsForItemType( payloadConfig.itemId ),
                  contextMenuConfig      = this.getAddItemDialogConfig( itemTypeName, allowedReferenceSetIds, node, schema ),
                  allowAdd               = !contextMenuConfig.disableAddExistingItem
                      || !contextMenuConfig.disableAddNewItem,
                  allowDelete            = !!payloadConfig?.referenceTypeName
                      && (
                          !contextMenuConfig.disableDeleteReference
                          || !contextMenuConfig.disableDeleteItem
                          || !contextMenuConfig.disableDeleteDescendantReferences
                          || !contextMenuConfig.disableDeleteDescendantItems
                          || itemId < 0
                      );

            if ( showOnly !== 'delete' && allowAdd && !contextMenuConfig.disableAddNewItem ) {
                buttonId = `${uid}_${nodeId}_addButton`;
                menuItemId = `${nodeId}_addItem`;

                retVal.push( {
                    id     : menuItemId,
                    label  : __( 'YN.base.designer.tree.item.add.title' ),
                    icon   : 'pi pi-plus-circle yn-green',
                    data   : {
                        buttonId
                    },
                    command: ( e ) => this.nodeButtons[ e.item.data.buttonId! ].content?.expand( new MouseEvent( 'click', { altKey: false, ctrlKey: false, shiftKey: false } ) as unknown as React.MouseEvent )
                } );
            }

            if ( showOnly !== 'add' && allowDelete ) {
                let firstPayloadId : number,
                    firstPayloadItem : Item;

                buttonId = `${uid}_${nodeId}_deleteButton`;

                if ( retVal.length ) {
                    retVal.push( { separator: true } );
                }

                // undelete option
                if ( nodeIsDeleted ) {
                    menuItemId = `${nodeId}_undeleteItem`;

                    retVal.push( {
                        id     : menuItemId,
                        label  : __( 'YN.base.designer.tree.item.undelete.title' ),
                        icon   : 'pi pi-plus-circle yn-dirty',
                        data   : {
                            buttonId,
                            confirmTitle: __( `YN.base.designer.tree.item.undelete.text` )
                        },
                        command: ( e : MenuItemCommandEvent ) => {
                            if ( !( e.originalEvent as React.SyntheticEvent & KeyboardEvent ).altKey ) {
                                this.nodeButtons[ e.item.data.buttonId! ].content?.expand( e.originalEvent as unknown as React.MouseEvent );
                            }
                            else {
                                this.deleteOrUndeleteNode( false, nodeId, 'single', true );
                            }
                        }
                    } );
                }
                else if ( node && ( !contextMenuConfig.disableDeleteItem || itemId < 0 ) ) {
                    firstPayloadId = node.data.payloadId;
                    firstPayloadItem = global.data[ dataContext ]?.items[ firstPayloadId ];

                    menuItemId = `${nodeId}_deleteItem`;

                    retVal.push( {
                        id     : menuItemId,
                        label  : __( 'YN.base.designer.tree.item.delete.title' ),
                        icon   : 'pi pi-minus-circle yn-delete',
                        data   : {
                            buttonId,
                            confirmTitle: __( 'YN.base.designer.tree.item.delete.text', { itemKey: firstPayloadItem.key, itemName: firstPayloadItem.name, itemDisplayAttributeValue: firstPayloadItem.displayAttributeValue } )
                        },
                        command: ( e : MenuItemCommandEvent ) => {
                            if ( !( e.originalEvent as React.SyntheticEvent & KeyboardEvent ).altKey ) {
                                this.nodeButtons[ e.item.data.buttonId! ].content?.expand( e.originalEvent as unknown as React.MouseEvent );

                                if ( this.nodeButtonPanels[ e.item.id! ] ) {
                                    setTimeout(
                                        () => {
                                            this.nodeButtonPanels[ e.item.id! ].expand( e.originalEvent as unknown as React.MouseEvent );
                                        },
                                        12
                                    );
                                }
                            }
                            else {
                                this.deleteOrUndeleteNode( false, nodeId, 'single', true );
                            }
                        }
                    } );
                }
            }
        }

        return retVal;
    }

    getAddItemDialogConfig( targetItemTypeName? : string, allowedReferenceSetIds? : {
        [ referenceSetId : number ] : 'dest' | 'src'
    }, node? : ExtendTreeNode, useSchema? : Schema ) : ContextMenuConfig {
        const {
                  uid,
                  dataContext,
                  useElusiveDataFromDataContext
              }             = this.props,
              nodeItem      = node?.data?.ciItem,
              isDynamicNode = node?.cypherMatchNum !== undefined;

        let schema : Schema = useSchema ?? 'DATA';

        if ( !useSchema && isDynamicNode ) {
            const payloadItem = global.data[ dataContext ]?.items[ node.data.payloadId ];
            const dataObjectAttributes = dataExtraction.extractPropertiesOfItem( payloadItem );
            schema = ( dataObjectAttributes.objectSchema ?? 'data' ).toUpperCase() as Schema;
        }

        let retVal : ContextMenuConfig              = {
                schema,
                disableDeleteItem                : true,
                disableDeleteReference           : true,
                disableDeleteDescendantItems     : true,
                disableDeleteDescendantReferences: true,
                disableAddExistingItem           : true,
                disableAddNewItem                : true
            } as ContextMenuConfig,
            referenceDirection : ( 'src' | 'dest' ) = 'src';

        // check if relevant properties has been changed

        // check if targetItemTyp is valid for allowed reference sets
        const targetItemTypeId = metaModel[ schema ].getItemType( targetItemTypeName ?? '' )?.id ?? 0;

        // allow only reference sets that have the targetItemType as destination
        const includeReferenceSets = Object.keys( allowedReferenceSetIds ?? {} )
            .filter( cirsId => {
                const srcOrDestItemTypeId = allowedReferenceSetIds![ cirsId ]! === 'src' ? 'srcItemTypeId' : 'destItemTypeId',
                      include             = metaModel[ schema ].getReferenceSet( cirsId * 1 )?.[ srcOrDestItemTypeId ] === targetItemTypeId;

                if ( include ) {
                    referenceDirection = allowedReferenceSetIds![ cirsId ]! as ( 'src' | 'dest' ) === 'src' ? 'dest' : 'src';
                }

                return include;
            } )
            .map( cirsId => cirsId * 1 );

        retVal = {
            ...retVal,
            includeReferenceSets,
            // get disableDelete* directly from appropriate node
            ...( isDynamicNode && nodeItem ? _.pick( nodeItem, [ 'disableDeleteItem', 'disableDeleteReference' ] ) : undefined ),
            ...( nodeItem ? _.pick( nodeItem, [ 'disableDeleteDescendantItems', 'disableDeleteDescendantReferences' ] ) : undefined ),
            // get disableAdd* from child nodes
            ...( node ? this.treeUtils?.getAddPermissions( node ) : undefined )
        };

        if ( node?.data?.ciItem ) {
            const nodeItemTypeId = global.data[ ( useElusiveDataFromDataContext ? dataContext : uid ) + '-meta' ]?.items[ node.data.ciItem.id ]?.itemTypeId;
            if ( nodeItemTypeId ) {
                if ( isDynamicNode ) {
                    retVal.disableDeleteItem = retVal.disableDeleteItem ?? metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( nodeItemTypeId, 'DisableDeleteItem' )?.valueDefault;
                    retVal.disableDeleteReference = retVal.disableDeleteReference ?? metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( nodeItemTypeId, 'DisableDeleteReference' )?.valueDefault;
                }
                retVal.disableDeleteDescendantItems = retVal.disableDeleteDescendantItems ?? metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( nodeItemTypeId, 'DisableDeleteDescendantItems' )?.valueDefault;
                retVal.disableDeleteDescendantReferences = retVal.disableDeleteDescendantReferences ?? metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( nodeItemTypeId, 'DisableDeleteDescendantReferences' )?.valueDefault;
            }
        }

        // TODO: dynamic handling of referenceDirection like in AddTreeNodeDialog
        const possibleItemTypes = _.uniq(
            includeReferenceSets.map( cirsId => metaModel[ schema ].getReferenceSet( cirsId )?.[ referenceDirection === 'src' ? 'srcItemTypeId' : 'destItemTypeId' ] ?? 0 )
                .map( citId => metaModel[ schema ].getItemType( citId )?.name ?? '' )
        );

        if ( possibleItemTypes.length ) {
            retVal.disableAddExistingItem = false;
            retVal.disableAddNewItem = false;
        }

        // TODO: check cardinality
        retVal.allowClone = isDynamicNode;

        // check for conditional permissions
        if ( node ) {
            retVal = { ...retVal, ...this.treeUtils?.getConditionalNodePermissions( node, retVal as NodePermissions ) };
        }

        return retVal;
    }

    getAddNodeButton( nodeId : string, dataObjectItemId : number ) {
        const {
                  uid
              }          = this.props,
              buttonId   = `${uid}_${nodeId}_addButton`,
              getContent = () => {
                  const stateToUse = this.addNode( nodeId ),
                        {
                            dataContext,
                            schema
                        }          = this.props,
                        {
                            addItemDialogConfig
                        }          = stateToUse;

                  return getAddItemDialog( {
                      uid                   : uid + '-AddItemReferenceDialog_button_content',
                      dataContext,
                      schema                : schema!,
                      addMode               : 'new',
                      rawMode               : false,
                      showAdditionalRawNames: false,
                      useThisLabel          : false,
                      onAddNode             : this.onAddNewNode.bind( this, dataObjectItemId ),
                      onHide                : () => {
                          this.nodeButtons[ buttonId ].content?.collapse( new MouseEvent( 'click' ) as unknown as React.MouseEvent );
                      },
                      onInteraction         : () => {
                          this.nodeButtons[ buttonId ].justInteracted = true;
                          this.ignoreSelectionChange = true;
                      },
                      ...addItemDialogConfig
                  } );
              };

        this.nodeButtons[ buttonId ] = {
            justInteracted: false
        };

        return <ContentButton
            key={buttonId}
            id={buttonId}

            ref={node => {
                if ( node ) {
                    this.nodeButtons[ buttonId ] = {
                        ...this.nodeButtons[ buttonId ],
                        content: node
                    };
                }
            }}

            icon="pi pi-plus-circle yn-add"
            rounded={true}
            text={true}
            horizontalContentAlign="left"
            verticalContentAlign="bottom"

            onExpand={( event ) => {
                const innerRetVal = !!getContent();

                if ( innerRetVal ) {
                    this.ignoreSelectionChange = true;
                }
                else {
                    this.ignoreSelectionChange = false;
                }

                return innerRetVal;
            }}
            onCollapse={( event, isOutsideClicked ) => {
                const innerRetVal = !this.nodeButtons[ buttonId ].justInteracted && isOutsideClicked;

                this.nodeButtons[ buttonId ].justInteracted = false;

                if ( !innerRetVal ) {
                    this.ignoreSelectionChange = true;
                }
                else {
                    this.ignoreSelectionChange = false;
                }

                return innerRetVal;
            }}
            content={getContent}
        />;
    }

    getDeleteNodeButton( nodeId : string ) {
        const {
                  uid
              }                = this.props,
              buttonId         = `${uid}_${nodeId}_deleteButton`,
              contentMenuItems = this.getTreeContextMenu( nodeId, 'delete' );

        let retVal : React.ReactElement;

        this.nodeButtons[ buttonId ] = {
            justInteracted: false
        };

        if ( contentMenuItems.length === 1 ) {
            const key = contentMenuItems[ 0 ].id!;

            retVal = <ContentButton
                key={buttonId}
                id={buttonId}

                ref={node => {
                    if ( node ) {
                        this.nodeButtons[ buttonId ] = {
                            ...this.nodeButtons[ buttonId ],
                            content: node
                        };
                    }
                }}

                icon={contentMenuItems[ 0 ].icon}
                rounded={true}
                text={true}
                horizontalContentAlign="left"
                verticalContentAlign="bottom"

                onExpand={( event ) => {
                    let innerRetVal : boolean = true;

                    if ( event.altKey || !contentMenuItems[ 0 ].data.confirmTitle ) {
                        contentMenuItems[ 0 ].command?.( {
                            originalEvent: new MouseEvent( 'click', { altKey: event.altKey } ) as unknown as React.MouseEvent,
                            item         : contentMenuItems[ 0 ]
                        } );

                        innerRetVal = false;
                    }

                    if ( innerRetVal ) {
                        this.ignoreSelectionChange = true;
                    }
                    else {
                        this.ignoreSelectionChange = false;
                    }

                    return innerRetVal;
                }}

                content={contentMenuItems[ 0 ].data.confirmTitle
                    ? <Panel
                        key={key}
                        id={`${uid}_${key}_panel`}

                        style={{ width: '25rem' }}
                        className="yn-confirm-option-panel"
                    >
                        <label>
                            <span className="pi pi-question-circle yn-blue"></span>
                            {contentMenuItems[ 0 ].data.confirmTitle}
                        </label>
                        <Button
                            label={__( 'YN.base.common.no' )}
                            className="p-button-sm p-button-secondary mr-3"
                            rounded={false}
                            text={false}
                            outlined={true}
                            onClick={( event ) => {
                                this.nodeButtons[ buttonId ].content?.collapse( new MouseEvent( 'click' ) as unknown as React.MouseEvent );
                            }}
                        />
                        <Button
                            label={__( 'YN.base.common.yes' )}
                            className="p-button-sm p-confirm-popup-accept"
                            rounded={false}
                            text={false}
                            outlined={true}
                            onClick={( event ) => {
                                contentMenuItems[ 0 ].command?.( {
                                    originalEvent: new MouseEvent( 'click', { altKey: true } ) as unknown as React.MouseEvent,
                                    item         : contentMenuItems[ 0 ]
                                } );

                                this.nodeButtons[ buttonId ].content?.collapse( new MouseEvent( 'click' ) as unknown as React.MouseEvent );
                            }}
                        />
                    </Panel>
                    : null}
            />;
        }
        else {
            const buttonContent : React.ReactNode[] = [];

            for ( let i = 0; i < contentMenuItems.length; i++ ) {
                const key = contentMenuItems[ i ].id!;

                delete this.nodeButtonPanels[ key ];

                buttonContent.push(
                    <Panel
                        key={key}
                        id={`${uid}_${key}_panel`}

                        ref={node => {
                            if ( node ) {
                                this.nodeButtonPanels[ key ] = node;
                            }
                        }}

                        header={
                            <Button
                                label={contentMenuItems[ i ].label}
                                icon={contentMenuItems[ i ].icon}
                                rounded={false}
                                text={true}
                                className="yn-menu-button yn-no-rounded-borders"
                                onClick={( event ) => {
                                    if ( event.altKey || !contentMenuItems[ i ].data.confirmTitle ) {
                                        contentMenuItems[ i ].command?.( {
                                            originalEvent: new MouseEvent( 'click', { altKey: event.altKey } ) as unknown as React.MouseEvent,
                                            item         : contentMenuItems[ i ]
                                        } );

                                        this.nodeButtons[ buttonId ].content?.collapse( new MouseEvent( 'click' ) as unknown as React.MouseEvent );
                                    }
                                    else {
                                        for ( const dPanelId of Object.keys( this.nodeButtonPanels ) ) {
                                            if ( dPanelId !== key ) {
                                                this.nodeButtonPanels[ dPanelId ].collapse( event );
                                            }
                                            else {
                                                this.nodeButtonPanels[ dPanelId ].toggle( event );
                                            }
                                        }
                                    }
                                }}
                            />
                        }

                        toggleable={true}
                        collapsed={true}
                        className="yn-confirm-option-panel"
                        pt={{
                            root: {
                                style: {
                                    padding: '0'
                                }
                            }
                        }}
                    >
                        {contentMenuItems[ i ].data.confirmTitle &&
                            <>
                                <label>
                                    <span className="pi pi-question-circle"></span>
                                    {contentMenuItems[ i ].data.confirmTitle}
                                </label>
                                <Button
                                    label={__( 'YN.base.common.no' )}
                                    className="p-button-sm p-button-secondary mr-3"
                                    rounded={false}
                                    text={false}
                                    outlined={true}
                                    onClick={( event ) => {
                                        this.nodeButtons[ buttonId ].content?.collapse( new MouseEvent( 'click' ) as unknown as React.MouseEvent );
                                    }}
                                />
                                <Button
                                    label={__( 'YN.base.common.yes' )}
                                    className="p-button-sm p-confirm-popup-accept"
                                    rounded={false}
                                    text={false}
                                    outlined={true}
                                    onClick={( event ) => {
                                        contentMenuItems[ i ].command?.( {
                                            originalEvent: new MouseEvent( 'click', { altKey: true } ) as unknown as React.MouseEvent,
                                            item         : contentMenuItems[ i ]
                                        } );

                                        this.nodeButtons[ buttonId ].content?.collapse( new MouseEvent( 'click' ) as unknown as React.MouseEvent );
                                    }}
                                />
                            </>
                        }
                    </Panel>
                );
            }

            retVal = <ContentButton
                key={buttonId}
                id={buttonId}

                ref={node => {
                    if ( node ) {
                        this.nodeButtons[ buttonId ] = {
                            ...this.nodeButtons[ buttonId ],
                            content: node
                        };
                    }
                }}

                icon="pi pi-minus-circle yn-delete"
                rounded={true}
                text={true}
                horizontalContentAlign="left"
                verticalContentAlign="bottom"

                content={
                    <Panel
                        style={{ width: '25rem' }}
                        pt={{
                            content: {
                                style: {
                                    border : 'none',
                                    padding: '.5rem 1rem'
                                }
                            }
                        }}
                    >
                        {buttonContent}
                    </Panel>
                }
            />;
        }

        return retVal;
    }

    onAddNewNode( dataObjectItemId : number, item : ItemAttributesForCreation | Item, reference : ReferenceAttributesForCreation ) {
        const {
                  uid,
                  dataContext,
                  schema,
                  useElusiveDataFromDataContext,
                  apiParams
              }                          = this.props,
              dataObjectItem             = global.data[ dataContext ]?.items[ dataObjectItemId ],
              objectSchemaPropertyTypeId = metaModel[ 'CFG' ].getPropertyType( 'ObjectSchema' )!.id;

        if ( dataObjectItem ) {
            const objectSchema = ( dataObjectItem.properties.get( objectSchemaPropertyTypeId )?.value as string ).toUpperCase() as Schema ?? schema,
                  objectType   = metaModel[ objectSchema ].getItemType( item.itemTypeId )?.name,
                  referenceSet = metaModel[ objectSchema ].getReferenceSet( reference.referenceSetId );

            if ( objectSchema && objectType && referenceSet ) {
                const rootDataObjectItem                  = global.data[ dataContext ]?.items[ ( apiParams.rootItemId ?? 0 ) as number ],
                      activeStatusId                      = metaModel[ 'CFG' ].getStatus( 'active' )!.id,
                      dataObjectItemTypeId                = metaModel[ 'CFG' ].getItemType( 'DataObject' )!.id,
                      objectTypePropertySetId             = metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( dataObjectItemTypeId, 'ObjectType' )!.id,
                      objectSchemaPropertySetId           = metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( dataObjectItemTypeId, 'ObjectSchema' )!.id,
                      isReferencedToReferenceSetId        = metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( dataObjectItemTypeId, dataObjectItemTypeId, 'isReferencedTo', 'dest' )![ 0 ].id,
                      referenceConfigurationPropertySetId = metaModel[ 'CFG' ].getReferencePropertySetForReferenceSetAndPropertyType( isReferencedToReferenceSetId, 'ReferenceConfiguration' )!.id,
                      referenceDirection                  = referenceSet.srcItemType.name === objectType ? 'SrcToDest' : 'DestToSrc'; // the direction of the referenceSet seen from the DataObject view (new DataObject (src) - belongsTo -> parent DataObject (dest))

                // parent DataObject <- isReferencedTo - DataObject
                // Organization <- belongsTo - Activity => ok
                // Department - belongsTo -> Organization => ok (?)

                let designerKey = '';

                for ( const referenceId of rootDataObjectItem?.references ?? [] ) {
                    const itemReference = global.data[ dataContext ]?.references[ referenceId ];

                    if ( itemReference?.referenceSet.referenceType.name === 'isRootOf' ) {
                        designerKey = global.data[ dataContext ]?.items[ itemReference.destItemId ].key;
                        break;
                    }
                }

                const newItem = this.props.addItem?.(
                    dataContext,
                    {
                        statusId  : activeStatusId,
                        itemTypeId: dataObjectItemTypeId,
                        key       : `${designerKey}:${objectType}:${uuidV4().substring( 0, 8 )}`,
                        name      : objectType,
                        properties: [
                            {
                                propertySetId: objectTypePropertySetId,
                                value        : objectType,
                                statusId     : activeStatusId
                            },
                            {
                                propertySetId: objectSchemaPropertySetId,
                                value        : objectSchema.toLowerCase(),
                                statusId     : activeStatusId
                            }
                        ]
                    },
                    schema!,
                    useElusiveDataFromDataContext
                        ? undefined
                        : [ uid ]
                );

                this.props.addReference?.(
                    dataContext,
                    {
                        referenceSetId: isReferencedToReferenceSetId,
                        statusId      : activeStatusId,
                        srcItemId     : newItem.id,
                        destItemId    : dataObjectItemId,
                        enumProperties: [
                            {
                                referenceId           : Reference.uniqueIdCount,
                                referencePropertySetId: referenceConfigurationPropertySetId,
                                properties            : [
                                    {
                                        referenceId           : Reference.uniqueIdCount,
                                        referencePropertySetId: referenceConfigurationPropertySetId,
                                        key                   : referenceSet?.referenceType.name,
                                        value                 : `{ "referenceDirection": "${referenceDirection}" }`,
                                        statusId              : activeStatusId
                                    }
                                ]
                            }
                        ]
                    },
                    schema!,
                    useElusiveDataFromDataContext
                        ? undefined
                        : [ uid ]
                );
            }
        }
    }

    getPayloadConfig( treeNodeData : ExtendTreeNode ) : PayloadConfig | undefined {
        let retVal;
        const
            { dataContext }                   = this.props,
            dataObjectId                      = treeNodeData.data.payloadId,
            dataObject                        = dataObjectId ? global.data[ dataContext ]?.items[ dataObjectId ] : undefined,
            dataObjectAttributes              = dataObject ? dataExtraction.extractPropertiesOfItem( dataObject ) : undefined,
            isReferencedToReferenceId         = treeNodeData.data.payloadReferenceId,
            isReferencedToReference           = global.data[ dataContext ]?.references[ isReferencedToReferenceId ],
            isReferencedToReferenceAttributes = isReferencedToReference ? dataExtraction.extractPropertiesOfReference( isReferencedToReference ) : undefined,
            parentDataObjectId                = isReferencedToReferenceAttributes?.destItemId,
            parentDataObject                  = parentDataObjectId ? global.data[ dataContext ]?.items[ parentDataObjectId ] : undefined,
            parentDataObjectAttributes        = parentDataObject ? dataExtraction.extractPropertiesOfItem( parentDataObject ) : undefined,
            rootDataObjectId                  = ( this.props.apiParams.rootItemId ?? 0 ) as number,
            rootDataObject                    = rootDataObjectId ? global.data[ dataContext ]?.items[ rootDataObjectId ] : undefined,
            rootDataObjectAttributes          = rootDataObject ? dataExtraction.extractPropertiesOfItem( rootDataObject ) : undefined,
            schema : Schema                   = ( dataObjectAttributes?.objectSchema ?? parentDataObjectAttributes?.objectSchema ?? rootDataObjectAttributes?.objectSchema ?? 'data' ).toUpperCase();

        if ( dataObjectAttributes?.objectType ) {
            const
                refConfig          = isReferencedToReferenceAttributes?.referenceConfiguration as ReferenceConfiguration,
                referenceTypeName  = refConfig ? Object.keys( refConfig )[ 0 ] as string : undefined,
                referenceDirection = referenceTypeName ? refConfig?.[ referenceTypeName ]!.referenceDirection as 'SrcToDest' | 'DestToSrc' ?? 'SrcToDest' : undefined,
                referenceSet       = parentDataObjectAttributes?.objectType && referenceTypeName
                    ? metaModel[ schema ]?.getReferenceSetsForItemTypesAndReferenceType( dataObjectAttributes.objectType, parentDataObjectAttributes.objectType, referenceTypeName, referenceDirection === 'SrcToDest' ? 'src' : 'dest' )[ 0 ]
                    : undefined,
                reverseDirection   = referenceSet?.srcItemType.name === referenceSet?.destItemType.name
                    ? referenceDirection === 'DestToSrc'
                    : referenceSet?.srcItemType.name !== dataObjectAttributes.objectType;

            let referenceLabel = referenceSet?.label ?? '';

            if ( referenceSet ) {
                if ( referenceDirection === 'DestToSrc' ) {
                    referenceLabel = `${__( 'YN.base.finder.reference.directionMarker' )} ${referenceSet.referenceTypeLabel} ${__( 'YN.base.finder.reference.directionMarker' )} ${referenceSet.destItemType.label}`;
                }
                else {
                    referenceLabel = `${__( 'YN.base.finder.reference.directionMarkerReversed' )} ${referenceSet.referenceTypeLabel} ${__( 'YN.base.finder.reference.directionMarkerReversed' )} ${referenceSet.srcItemType.label}`;
                }
            }

            retVal = {
                itemId           : dataObjectId,
                itemTypeName     : dataObjectAttributes.objectType,
                itemTypeLabel    : metaModel[ schema ]?.getItemType( dataObjectAttributes.objectType )?.label ?? dataObjectAttributes.objectType,
                schema,
                referenceTypeName: referenceSet?.referenceTypeLabel ?? referenceTypeName,
                referenceDirection,
                reverseDirection,
                cardinality      : referenceSet?.cardinality,
                referenceLabel,
                referenceSet
            }
        }

        return retVal;
    }

    getAllowedReferenceSetsForItemType( itemId : number ) : { [ referenceSetId : number ] : 'dest' | 'src' } {
        const {
                  dataContext
              }    = this.props,
              item = global.data[ dataContext ]?.items?.[ itemId ];

        let retVal : { [ referenceSetId : number ] : 'dest' | 'src' } = {};

        if ( item ) {
            const itemData = dataExtraction.extractPropertiesOfItem( item );

            if ( itemData.objectSchema && itemData.objectType ) {
                const schema                                 = ( itemData.objectSchema as string ).toUpperCase() as Schema,
                      possibleReferenceSets                  = itemData.objectSchema && itemData.objectType
                          ? metaModel[ schema ].getReferenceSetsForItemType( itemData.objectType )
                          : [],
                      existingReferenceSets : ReferenceSet[] = [];

                for ( const referenceId of item.references ) {
                    const reference = global.data[ dataContext ]?.references[ referenceId ];

                    if (
                        reference
                        && reference.destItem === item
                        && reference.referenceSet.referenceType.name === 'isReferencedTo'
                        && reference.srcItem
                    ) {
                        const referenceConfigurationPropertyTypeId = metaModel[ reference.schema ].getPropertyType( 'ReferenceConfiguration' )?.id,
                              referenceConfiguration               = referenceConfigurationPropertyTypeId && reference.enumProperties.has( referenceConfigurationPropertyTypeId )
                                  ? [ ...reference.enumProperties.get( referenceConfigurationPropertyTypeId )!.properties.values() ][ 0 ]
                                  : undefined,
                              itemData2                            = dataExtraction.extractPropertiesOfItem( reference.srcItem );

                        if ( referenceConfiguration && itemData2.objectType ) {
                            try {
                                const referenceTypeName  = referenceConfiguration.key as string,
                                      referenceDirection = JSON.parse( String( referenceConfiguration.value ) )?.referenceDirection,
                                      referenceSet       = metaModel[ schema ].getReferenceSetsForItemTypesAndReferenceType(
                                          referenceDirection === 'SrcToDest' ? itemData2.objectType : itemData.objectType,
                                          referenceDirection === 'SrcToDest' ? itemData.objectType : itemData2.objectType,
                                          referenceTypeName,
                                          'src'
                                      )?.[ 0 ];

                                if ( referenceSet ) {
                                    existingReferenceSets.push(
                                        referenceSet
                                    );
                                }
                            }
                            catch ( e ) {
                                console.error( e );
                            }
                        }
                    }
                }

                retVal = possibleReferenceSets.filter( rSet => !existingReferenceSets.includes( rSet ) )
                    .reduce(
                        ( result, rset ) => result = { ...result, [ rset.id ]: rset.destItemType.name === itemData.objectType ? 'dest' : 'src' },
                        {}
                    );
            }
        }

        return retVal;
    }

    createDataFromConfig( dataConfig : DataConfig, context? : string ) {
        const retVal : {
            layoutElementId : number | undefined,
            data : {
                items : ItemCollection;
                references? : ReferenceCollection;
            } | undefined
        } = {
            layoutElementId: undefined,
            data           : undefined
        };

        const {
                  apiParams,
                  allElusiveData,
                  additionalElusiveDataContexts
              }                 = this.props,
              fieldItemTypeId   = metaModel[ 'CFG' ].getItemType( dataConfig.itemTypeName )?.id,
              layoutElementItem = fieldItemTypeId
                  ? new Item(
                      {
                          itemTypeId: fieldItemTypeId,
                          key       : `${apiParams.rootItemKey}:${dataConfig.key}:${this.getUniqueIdentifierPart()}`,
                          name      : dataConfig.name
                      },
                      context,
                      'CFG',
                      !context
                  )
                  : undefined;

        if ( layoutElementItem ) {
            retVal.data = {
                items     : {},
                references: {}
            };

            let referenceSetId = metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( dataConfig.itemTypeName, 'Overview', 'belongsTo', 'src' )?.[ 0 ]?.id,
                reference      = referenceSetId && apiParams.rootItemId
                    ? new Reference(
                        {
                            referenceSetId,
                            srcItemId : layoutElementItem.id,
                            destItemId: apiParams.rootItemId as number
                        },
                        context,
                        'CFG',
                        !context
                    )
                    : undefined;

            // reference to Overview
            if ( reference ) {
                layoutElementItem.registerReference( reference );

                retVal.layoutElementId = layoutElementItem.id;
                retVal.data.items[ layoutElementItem.id ] = layoutElementItem;
                retVal.data.references![ reference.id ] = reference;

                // reference to DataObject
                if ( dataConfig.dataObjectId ) {
                    referenceSetId = metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( 'DataObject', dataConfig.itemTypeName, 'providesDataFor', 'src' )?.[ 0 ]?.id;
                    reference = referenceSetId && dataConfig.dataObjectId !== undefined
                        ? new Reference(
                            {
                                referenceSetId,
                                srcItemId : dataConfig.dataObjectId,
                                destItemId: layoutElementItem.id
                            },
                            context,
                            'CFG',
                            !context
                        )
                        : undefined;

                    if ( reference ) {
                        layoutElementItem.registerReference( reference );

                        retVal.data.references![ reference.id ] = reference;
                    }
                }
            }

            if ( Object.keys( retVal.data.items ).length ) {
                if ( dataConfig.properties ) {
                    for ( const propertyTypeName of Object.keys( dataConfig.properties ) ) {
                        const propertyTypeId = metaModel[ 'CFG' ].getPropertyType( propertyTypeName )?.id;

                        if ( propertyTypeId ) {
                            layoutElementItem.addProperty( propertyTypeId ).value = dataConfig.properties[ propertyTypeName ];
                        }
                    }
                }

                if ( dataConfig.enumProperties ) {
                    for ( const propertyTypeName of Object.keys( dataConfig.enumProperties ) ) {
                        const propertyTypeId = metaModel[ 'CFG' ].getPropertyType( propertyTypeName )?.id;

                        if ( propertyTypeId ) {
                            const enumProperty = layoutElementItem.addEnumProperty( propertyTypeId );

                            for ( const propertyDef of dataConfig.enumProperties[ propertyTypeName ] ) {
                                const property = enumProperty.addProperty( propertyDef.key ?? '' );

                                if ( propertyDef.value ) {
                                    property.value = propertyDef.value;
                                }
                            }
                        }
                    }
                }

                if ( dataConfig.additionalData?.length ) {
                    const processAdditionalDataConfigRecursive = ( additionalDataConfig : Required<DataConfig>[ 'additionalData' ], parentItem : Item ) => {
                        for ( const payload of additionalDataConfig ) {
                            const itemTypeId = metaModel[ 'CFG' ].getItemType( payload.itemTypeName )?.id,
                                  item       = itemTypeId
                                      ? new Item(
                                          {
                                              itemTypeId,
                                              key : `${apiParams.rootItemKey}:${payload.key}:${this.getUniqueIdentifierPart()}`,
                                              name: payload.name
                                          },
                                          context,
                                          'CFG',
                                          !context
                                      )
                                      : undefined;

                            referenceSetId = metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( payload.itemTypeName, parentItem.itemTypeId, payload.referenceTypeName, payload.referenceDirection )?.[ 0 ]?.id;
                            reference = item && referenceSetId
                                ? new Reference(
                                    {
                                        referenceSetId,
                                        srcItemId : payload.referenceDirection === 'src'
                                            ? item.id
                                            : parentItem.id,
                                        destItemId: payload.referenceDirection === 'dest'
                                            ? item.id
                                            : parentItem.id
                                    },
                                    context,
                                    'CFG',
                                    !context
                                )
                                : undefined;

                            if ( item && reference ) {
                                parentItem.registerReference( reference );
                                item.registerReference( reference );

                                retVal.data!.items[ item.id ] = item;
                                retVal.data!.references![ reference.id ] = reference;

                                if ( payload.properties ) {
                                    for ( const propertyTypeName of Object.keys( payload.properties ) ) {
                                        const propertyTypeId = metaModel[ 'CFG' ].getPropertyType( propertyTypeName )?.id;

                                        if ( propertyTypeId ) {
                                            item.addProperty( propertyTypeId ).value = payload.properties[ propertyTypeName ];
                                        }
                                    }
                                }

                                if ( payload.enumProperties ) {
                                    for ( const propertyTypeName of Object.keys( payload.enumProperties ) ) {
                                        const propertyTypeId = metaModel[ 'CFG' ].getPropertyType( propertyTypeName )?.id;

                                        if ( propertyTypeId ) {
                                            const enumProperty = item.addEnumProperty( propertyTypeId );

                                            for ( const propertyDef of payload.enumProperties[ propertyTypeName ] ) {
                                                const property = enumProperty.addProperty( propertyDef.key ?? '' );

                                                if ( propertyDef.value ) {
                                                    property.value = propertyDef.value;
                                                }
                                            }
                                        }
                                    }
                                }

                                if ( payload.additionalData?.length ) {
                                    processAdditionalDataConfigRecursive( payload.additionalData, item );
                                }
                            }
                        }
                    };

                    processAdditionalDataConfigRecursive( dataConfig.additionalData, layoutElementItem );
                }

                if ( context ) {
                    const elusiveDataContexts = ( [] as string[] ).concat( additionalElusiveDataContexts );

                    elusiveDataContexts.unshift( context );

                    for ( const elusiveDataContext of elusiveDataContexts ) {
                        const elusiveData    = allElusiveData?.[ elusiveDataContext ],
                              newElusiveData = {
                                  ...elusiveData
                              };

                        for ( const item of Object.values( retVal.data.items ) ) {
                            newElusiveData.data = {
                                ...newElusiveData?.data,
                                items     : {
                                    ...newElusiveData?.data?.items,
                                    [ item.id ]: ( newElusiveData?.data?.items?.[ item.id ] ?? 0 ) + 1
                                },
                                references: newElusiveData?.data?.references ?? {}
                            };
                        }

                        for ( reference of Object.values( retVal.data.references! ) ) {
                            newElusiveData.data = {
                                ...newElusiveData?.data,
                                items     : newElusiveData?.data?.items ?? {},
                                references: {
                                    ...newElusiveData?.data?.references,
                                    [ reference.id ]: ( newElusiveData?.data?.references?.[ reference.id ] ?? 0 ) + 1
                                }
                            };
                        }

                        this.props.setElusiveData( elusiveDataContext, newElusiveData );
                    }
                }
            }
        }

        return retVal;
    }

    onTreeNodeToggleClick( key : string ) {
        const node             = this.treeUtils?.getNodeById( key ),
              payloadItem      = global.data[ this.props.dataContext ].items[ node?.data.payloadId ],
              newSelectionKeys = ( [] as string[] ).concat( this.props.selectionKeys ?? [] ),
              index            = newSelectionKeys.findIndex( ( selectedKey ) => selectedKey === key );

        if ( index !== -1 ) {
            newSelectionKeys.splice( index, 1 );
        }
        else {
            newSelectionKeys.push( key );
        }

        // deleted nodes could not be selected
        if ( !payloadItem || payloadItem.statusId !== -1 ) {
            this.props.setParams?.( this.props.uid, {
                selectionKeys: newSelectionKeys
            } );
        }
    }

    onDoubleClick( dataConfig : DataConfig, cancel : boolean = false ) {
        if ( cancel ) {
            return;
        }

        const {
                  apiParams,
                  dataContext
              }       = this.props,
              columns = apiParams.rootItemId ? overviewUtilities.getColumns( dataContext, Number( apiParams.rootItemId ) ) : undefined;

        if ( columns !== undefined ) {

            // append column by getting max order number and increment it by 10
            dataConfig.properties = {
                ...dataConfig.properties,
                Order: columns.length
                    ? Math.max( ...columns.map( rC => rC.data.order ?? 0 ) ) + 10
                    : 10
            };

            const {
                      layoutElementId
                  } = this.createDataFromConfig( dataConfig, dataContext );

            this.props.onSelectLayoutElement?.( layoutElementId );
        }
    }

    /**
     * Event called on drag starts.
     *
     * @param {DataConfig} dataConfig
     * @param {DragOptions} options
     * @param {React.DragEvent<HTMLParagraphElement>} [event]
     * @param {boolean} [cancel=false]
     * @param {string} [context]
     */
    onDragStart( dataConfig : DataConfig, options : DragOptions, event : React.DragEvent<HTMLParagraphElement>, cancel : boolean = false ) {
        if ( cancel ) {
            event?.preventDefault();
            event?.stopPropagation();
            event?.nativeEvent.preventDefault();
            return;
        }

        const {
                  layoutElementId,
                  data
              } = this.createDataFromConfig( dataConfig );

        if ( layoutElementId && data ) {
            this.props.onNewLayoutElementDragStart?.(
                layoutElementId,
                data,
                options ?? {},
                event
            );

            return;
        }

        event?.preventDefault();
        event?.stopPropagation();
        event?.nativeEvent.preventDefault();
    }

    onTreeNodeContentScroll( treeNodeKey : string, event : React.UIEvent<HTMLDivElement> ) {
        const elusiveData = { ...this.props.elusiveData };

        if ( this.props.useElusiveDataFromDataContext ) {
            this.props?.setElusiveData( this.props.dataContext, {
                scrollPositions: {
                    ...elusiveData.scrollPositions,
                    [ treeNodeKey ]: ( event.target as HTMLDivElement ).scrollTop
                }
            } );
        }
        else {
            this.props?.setElusiveData( this.props.uid, {
                scrollPositions: {
                    ...elusiveData.scrollPositions,
                    [ treeNodeKey ]: ( event.target as HTMLDivElement ).scrollTop
                }
            } );
        }
    }

    getUniqueIdentifierPart() {
        return uuidV4().substring( 0, 8 );
    }

    /**
     * Template for tree nodes.
     *
     * @private
     */
    treeNodeTemplate( treeNodeData : TreeNode.TreeNode, options : TreeNodeTemplateOptions ) : React.ReactNode {
        const {
                  apiParams,
                  dataContext,
                  readOnly,
                  selectionKeys,
                  selectedLayoutElementId,
                  showTypeLabels,
                  showReferenceFields,
                  showMetaFields
              } = this.props;

        let aliasesInUse : string[]               = [],
            aliasesNotUsable : string[]           = [],
            tooltip : React.ReactNode | undefined = undefined,
            content : React.ReactElement | string = '';

        // handle DataObjects
        if ( treeNodeData.data?.payloadId ) {
            const payloadItem  = global.data[ dataContext ]?.items[ treeNodeData.data.payloadId ],
                  isDataObject = payloadItem?.itemType.name === 'DataObject';

            if ( payloadItem && isDataObject ) {
                const payloadConfig = treeNodeData.data.payloadConfig;

                // tooltip
                if ( payloadConfig?.referenceLabel ) {
                    tooltip = <Tooltip target={`#${treeNodeData.key}`} showDelay={500} position="top">
                        {payloadConfig.referenceLabel}
                    </Tooltip>;
                }
            }
        }

        // get usages
        const columns = apiParams.rootItemId ? overviewUtilities.getColumns( dataContext, apiParams.rootItemId as number ) : undefined;

        if ( columns?.length ) {
            for ( const column of columns ) {
                if ( column?.dataDescriptor ) {
                    aliasesInUse = aliasesInUse.concat( [ column.dataDescriptor.alias ] ).concat( column.dataDescriptor.fullAlias.split( ',' ) );

                    if ( !column.dataDescriptor.aggregate && column.dataDescriptor.type !== 'expression' ) {
                        aliasesNotUsable = aliasesNotUsable.concat( [ column.dataDescriptor.alias ] ).concat( column.dataDescriptor.fullAlias.split( ',' ) );
                    }
                }
                else if ( column.data.queryFieldName ) {
                    aliasesInUse.push( column.data.queryFieldName );
                    aliasesNotUsable.push( column.data.queryFieldName );
                }
            }
        }

        // build content if selected
        if ( selectionKeys?.includes( treeNodeData.key as string ) ) {
            let lastType : ColumnDefinition['type'] | undefined;

            content = <div className="yn-node-content">
                <div
                    id={`${treeNodeData.key}_scrollContainer`}
                    className="yn-treenode-container"
                    onScroll={( e ) => {
                        this.onTreeNodeContentScroll( String( treeNodeData.key ?? '' ), e );
                    }}
                >
                    {( treeNodeData as ExtendedTreeNodeData ).data.columnDefinition.map( ( definition : ColumnDefinition ) => {
                        const retVal : React.ReactNode[] = [];

                        if (
                            (
                                showReferenceFields
                                || (
                                    definition.type !== 'reference'
                                    && definition.type !== 'referenceProperty'
                                    && definition.type !== 'referenceImage'
                                    && definition.type !== 'referenceMeta'
                                )
                            )
                            && (
                                showMetaFields
                                || (
                                    definition.type !== 'meta'
                                    && definition.type !== 'referenceMeta'
                                )
                            )
                        ) {
                            const layoutElementClassNames : string[] = [ 'yn-designertree-field', 'yn-fieldlike' ],
                                  inUse                              = aliasesInUse.some( aIU => ( definition.dataDescriptorAlias ?? [] ).includes( aIU ) ),
                                  notUsable                          = aliasesNotUsable.some( aIU => ( definition.dataDescriptorAlias ?? [] ).includes( aIU ) );

                            if ( inUse ) {
                                layoutElementClassNames.push( 'yn-designertree-field-inuse' );
                            }
                            if ( notUsable ) {
                                layoutElementClassNames.push( 'yn-designertree-field-notusable' );
                            }

                            if ( definition.type === 'meta' || definition.type === 'referenceMeta' ) {
                                layoutElementClassNames.push( 'yn-designertree-field-meta' );
                            }

                            if ( definition.dataDescriptorAlias?.includes( this.state.selectedDataDescriptorAlias ?? '' ) ) {
                                layoutElementClassNames.push( 'yn-designertree-field-selected' );
                            }

                            if (
                                showTypeLabels
                                && lastType !== definition.type
                                && definition.type !== 'meta'
                                && definition.type !== 'referenceMeta'
                                && definition.type !== 'query'
                            ) {
                                retVal.push( <p
                                        key={definition.type}
                                        className="yn-designertree-field-group"
                                    >
                                        {__( `YN.base.frontend.designer.field.type.${definition.type}.label` )}
                                    </p>
                                );

                                lastType = definition.type;
                            }

                            retVal.push( <p
                                key={`${treeNodeData.key}_${definition.key}`}
                                data-datadescriptoralias={`${definition.dataDescriptorAlias?.join( ' ' ) ?? ''}`}
                                draggable={!readOnly}
                                onDoubleClick={readOnly
                                    ? undefined
                                    : ( e ) => {
                                        this.onDoubleClick( definition.data, inUse );
                                    }}
                                onDragStart={readOnly
                                    ? undefined
                                    : ( e ) => {
                                        this.onDragStart( definition.data, { label: definition.label, icon: definition.icon }, e, inUse );
                                    }}
                                onMouseEnter={() => {
                                    if ( definition.dataDescriptorAlias ) {
                                        this.props.onMarkLayoutElementsForAlias?.( definition.dataDescriptorAlias );
                                    }
                                }}
                                onMouseLeave={() => {
                                    this.props.onMarkLayoutElementsForAlias?.( undefined );
                                }}
                                className={layoutElementClassNames.join( ' ' )}
                            >
                                {getIconSpan( definition.icon )}{definition.label}
                            </p> );
                        }

                        return retVal;
                    } )}
                </div>
            </div>;
        }

        const classNames      = ( treeNodeData.className ?? '' ).split( ' ' ),
              labelClassNames = [ 'yn-label' ];

        if ( selectionKeys?.includes( treeNodeData.key as string ) ) {
            if ( !classNames.includes( 'yn-designertree-node-expanded' ) ) {
                classNames.push( 'yn-designertree-node-expanded' );
            }
        }
        else {
            const index = classNames.findIndex( className => className === 'yn-designertree-node-expanded' );

            if ( index !== -1 ) {
                classNames.splice( index, 1 );
            }
        }

        /* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
        if ( selectionKeys?.includes( treeNodeData.key as string ) || !aliasesInUse.length ) {
            const index = classNames.findIndex( className => className === 'yn-designertree-dataobject-inuse' );

            if ( index !== -1 ) {
                classNames.splice( index, 1 );
            }
        }
        else if ( aliasesInUse.length ) {
            if ( !classNames.includes( 'yn-designertree-dataobject-inuse' ) ) {
                classNames.push( 'yn-designertree-dataobject-inuse' );
            }
        }

        if ( treeNodeData.data.isDeleted && treeNodeData.data.isDirty ) {
            if ( !classNames.includes( 'yn-deleted' ) ) {
                classNames.push( 'yn-deleted' );
            }
        }
        else if ( treeNodeData.data.isDirty ) {
            labelClassNames.push( 'yn-dirty' );
        }
        else {
            let index = classNames.findIndex( className => className === 'yn-deleted' );

            if ( index !== -1 ) {
                classNames.splice( index, 1 );
            }

            index = classNames.findIndex( className => className === 'yn-dirty' );

            if ( index !== -1 ) {
                classNames.splice( index, 1 );
            }
        }

        if ( selectedLayoutElementId === treeNodeData.data.payloadId ) {
            if ( !classNames.includes( 'yn-designertree-node-selected' ) ) {
                classNames.push( 'yn-designertree-node-selected' );
            }
        }
        else {
            const index = classNames.findIndex( className => className === 'yn-designertree-node-selected' );

            if ( index !== -1 ) {
                classNames.splice( index, 1 );
            }
        }

        treeNodeData.className = classNames.join( ' ' );

        return <>
            {tooltip}
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
            <div
                className="flex flex-column flex-auto overflow-hidden yn-designertree-header"
                onClick={() => {
                    if ( !this.ignoreSelectionChange ) {
                        this.props.onSelectLayoutElement?.( treeNodeData.data.payloadId );
                    }

                    this.ignoreSelectionChange = false;
                }}
                onMouseEnter={() => {
                    if ( treeNodeData.data.dataDescriptorAlias ) {
                        this.props.onMarkLayoutElementsForAlias?.( treeNodeData.data.dataDescriptorAlias );
                    }
                }}
                onMouseLeave={() => {
                    this.props.onMarkLayoutElementsForAlias?.( undefined );
                }}
            >
                <div id={`${treeNodeData.key}`} className="yn-treenode-header">
                    <span key="label" className={labelClassNames.join( ' ' )}>{treeNodeData.label ?? ''}</span>
                    {treeNodeData.data.buttons}
                </div>
            </div>
            {content}
        </>;
    }

    treeTogglerTemplate( node : TreeNode.TreeNode, options : TreeTogglerTemplateOptions ) : React.ReactNode {
        const icon = IconUtils.getJSXIcon(
            this.props.selectionKeys?.includes( String( node.key ?? '' ) )
                ? <ChevronDownIcon/>
                : <ChevronRightIcon/>,
            {},
            {}
        );

        return <Button
            className={`yn-tree-toggler p-tree-toggler p-link p-unselectable-text ${node.selectable === false ? 'yn-disabled' : ''}`}
            icon={icon} onClick={this.onTreeNodeToggleClick.bind( this, String( node.key ?? '' ) )}></Button>
    }

    markFieldsForAlias( alias : string | string[] | undefined ) {
        this.setState( { aliasToMark: alias } );
    }

    getFooter( showTypeLabels : boolean, showReferenceFields : boolean, showMetaFields : boolean ) {
        const {
                  uid
              }                                          = this.props,
              layoutComponents : ToolbarLayoutComponents = {
                  left : [],
                  right: []
              };

        layoutComponents.left.push( {
            type: 'genericNode',

            params: <div
                key={`${uid}_showTypeLabelsCheckbox`}
                className="flex align-items-center field"
                style={{ width: '100%' }}
            >
                <Checkbox
                    checked={showTypeLabels}
                    onChange={this.onShowTypeLabelsCheckboxChange}
                    style={{ flex: '0 0 auto', width: 'auto', fontSize: '.8rem' }}
                />
                <label
                    htmlFor={`${uid}_showTypeLabelsCheckbox`}
                    className="block ml-3"
                    style={{ fontSize: '.8rem' }}
                >
                    {__( 'YN.base.frontend.overviewDesigner.showTypeLabels.label' )}
                </label>
            </div>
        } );

        layoutComponents.left.push( {
            type: 'genericNode',

            params: <div
                key={`${uid}_showReferenceFieldsCheckbox`}
                className="flex align-items-center field mt-2"
                style={{ width: '100%' }}
            >
                <Checkbox
                    checked={showReferenceFields}
                    onChange={this.onShowReferenceFieldsCheckboxChange}
                    style={{ flex: '0 0 auto', width: 'auto', fontSize: '.8rem' }}
                />
                <label
                    htmlFor={`${uid}_showReferenceFieldsCheckbox`}
                    className="block ml-3"
                    style={{ fontSize: '.8rem' }}
                >
                    {__( 'YN.base.frontend.overviewDesigner.showReferenceFields.label' )}
                </label>
            </div>
        } );

        layoutComponents.left.push( {
            type: 'genericNode',

            params: <div
                key={`${uid}_showMetaFieldsCheckbox`}
                className="flex align-items-center field mt-2"
                style={{ width: '100%' }}
            >
                <Checkbox
                    checked={showMetaFields}
                    onChange={this.onShowMetaFieldsCheckboxChange}
                    style={{ flex: '0 0 auto', width: 'auto', fontSize: '.8rem' }}
                />
                <label
                    htmlFor={`${uid}_showMetaFieldsCheckbox`}
                    className="block ml-3"
                    style={{ fontSize: '.8rem' }}
                >
                    {__( 'YN.base.frontend.overviewDesigner.showMetaFields.label' )}
                </label>
            </div>
        } );

        const toolbarComponents : ToolbarComponents = getToolbarComponents( layoutComponents );

        return <Toolbar
            start={toolbarComponents.left}
            className="yn-panel-toolbar shadow-4"
            style={{ zIndex: 5 }}
        />;
    }

    onShowMetaFieldsCheckboxChange( event : CheckboxChangeEvent ) {
        this.props.setParam( this.props.uid, 'showMetaFields', event.checked! );
    }

    onShowReferenceFieldsCheckboxChange( event : CheckboxChangeEvent ) {
        this.props.setParam( this.props.uid, 'showReferenceFields', event.checked! );
    }

    onShowTypeLabelsCheckboxChange( event : CheckboxChangeEvent ) {
        this.props.setParam( this.props.uid, 'showTypeLabels', event.checked! );
    }

    /**
     * React render method
     * @return {*}
     */
    render() {
        const {
                  treeData,
                  expandedKeys,
                  uid,
                  selectionKeys,
                  showTypeLabels,
                  showReferenceFields,
                  showMetaFields
              }      = this.props,
              {
                  contextMenuNode,
                  lastContextMenuNode
              }      = this.state,
              retVal = <>
                  <ContextMenu
                      key={`${uid}_contextMenu`}
                      model={this.getTreeContextMenu( lastContextMenuNode || contextMenuNode )}
                      ref={cm => this.cm = cm}
                      onHide={() => this.setState( { contextMenuNode: '' } )}
                  />
                  <ConfirmPopup/>
                  <div>
                      <Tree
                          key={`${uid}_tree`}
                          id={`${uid}_tree`}

                          ref={el => this.treeElement = el?.getElement() ?? undefined}

                          value={treeData}
                          nodeTemplate={this.treeNodeTemplate}

                          expandedKeys={expandedKeys}
                          togglerTemplate={this.treeTogglerTemplate}
                          // dummy on toggle needed to force PrimeReact tree to obey expanded keys property
                          onToggle={() => {
                          }}

                          selectionMode="multiple"
                          selectionKeys={( selectionKeys ?? [] ).reduce(
                              ( innerRetVal, key ) => {
                                  innerRetVal[ key ] = true;
                                  return innerRetVal;
                              },
                              {} as TreeMultipleSelectionKeys
                          )}

                          className="yn-menu-tree yn-node-content-tree yn-designertree"
                          dragdropScope="overviewdesigner"

                          contextMenuSelectionKey={contextMenuNode}
                          onContextMenuSelectionChange={e => {
                              this.setState( {
                                  contextMenuNode    : e.value + '',
                                  lastContextMenuNode: e.value + ''
                              } );
                          }}
                          onContextMenu={e => {
                              // disable context menu on static nodes
                              if ( ( e.node as ExtendTreeNode ).origId ) {
                                  this.cm?.show( e.originalEvent );
                              }
                          }}

                          footer={this.getFooter( showTypeLabels, showReferenceFields, showMetaFields )}
                          pt={{
                              footer: {
                                  style: {
                                      margin: '1rem -1rem -1rem -1rem',
                                      width : 'calc(100% + 2rem)',
                                      zIndex: '10'
                                  }
                              }
                          }}
                      />
                  </div>
              </>;

        if ( this.debug ) {
            console.log( `ItemTree - render - ${Math.round( 1000 * ( performance.now() - this.renderStartTime ) ) / 1000} ms`.asLogLine( 100 ) );
            this.renderStartTime = 0;
        }

        return retVal;
    }
}

// redux stuff

/**
 *
 * @param state
 * @param ownProps
 */
function mapStateToProps( state : StoreState, ownProps : ComponentProps ) : StateProps {
    const retVal : StateProps = {
        allElusiveData         : undefined,
        elusiveData            : undefined,
        intercomMessages       : undefined,
        messageCatalogTimestamp: 0,
        metaModelTimestamp     : 0,
        user                   : undefined,
        xhrRequestsForComponent: undefined
    };

    retVal.allElusiveData = state.elusiveData;

    if ( ownProps.uid ) {
        // get intercom messages
        if ( state.intercom?.[ ownProps.uid ] ) {
            retVal.intercomMessages = state.intercom[ ownProps.uid ];
        }

        if ( ownProps.useElusiveDataFromDataContext === false && state.elusiveData?.[ ownProps.uid ] ) {
            retVal.elusiveData = state.elusiveData[ ownProps.uid ];
        }
    }

    if ( ownProps.useElusiveDataFromDataContext !== false && state.elusiveData?.[ ownProps.dataContext ] ) {
        retVal.elusiveData = state.elusiveData[ ownProps.dataContext ];
    }

    if ( state.messagecatalog?.timestamp ) {
        retVal.messageCatalogTimestamp = state.messagecatalog.timestamp;
    }

    if ( state.metamodel?.timestamp ) {
        retVal.metaModelTimestamp = state.metamodel.timestamp;
    }

    // get some session user information
    if ( state.session?.user ) {
        retVal.user = state.session.user;
    }

    return retVal;
}

/**
 * Bind action creators to component props
 * @param dispatch
 * @return {*}
 */
function mapDispatchToProps( dispatch : Dispatch ) {
    return bindActionCreators( Actions, dispatch );
}

// apply mixin to TreeTable component
Object.assign( TreeTable.prototype, ActionMixin );

export default connect<StateProps, ActionProps, ComponentProps, StoreState>( mapStateToProps, mapDispatchToProps, undefined, { forwardRef: true } )( TreeTable );
